import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { fmtDisplayDateTime } from '@utils/displayDate'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import reportService from '@api/v1/reportService'
import reportJobService from '@api/v1/reportJobService'
import {
  ENDED_WITHOUT_RESULT, RUN_POLL_MS, readRunStatus, readFinishedResult, isJobStatusLost,
  STILL_RUNNING_LEAD, STILL_RUNNING_TAIL, FINISHED_UNREAD_LEAD, FINISHED_UNREAD_TAIL,
} from '@api/v1/reportJobRun'
import reportScheduleService from '@api/v1/reportScheduleService'
import QueueReportModal from '@components/reports/queue/QueueReportModal'
import ReportFilterPanel from '@components/reports/ReportFilterPanel'
import ReportTable from '@components/reports/ReportTable'
import ReportDataGrid from '@components/reports/ReportDataGrid'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import ReportExport from '@components/reports/ReportExport'
import ReportAiSummary from '@components/reports/ReportAiSummary'
import { aiScopeLine } from '@components/reports/aiScope'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import { dateBasisEnabled } from '@components/reports/reportFeatureConfig'
import usePreferencesStore from '@store/preferencesStore'
import { groupFlatReport, groupableColumns, resolveFilterLabel } from '@components/reports/reportGridUtils'
import { ReportRunStateContext } from '@components/reports/reportRunStateContext'
import LoadingSpinner from '@components/common/LoadingSpinner'
import useReadState from '@hooks/useReadState'
import { useRunTicket, ReopenTargetContext } from '@hooks/useLatestRun'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useBackNav from '@hooks/useBackNav'
import { useInvalidateSchedules } from '@hooks/useSchedules'
import useFirmName from '@hooks/useFirmName'
import { REPORTS_LIST_PATH } from '@config/reportRoutes'
import { progressLabel, runFailureMessage } from '@utils/reportProgress'
import { AI_REPORT_SUMMARY } from '@config/featureFlags'
import { useAiMode } from '@hooks/useUserSettings'
import { useColumnAliases } from '@hooks/useColumnAliases'
import { aliasDefinition } from '@utils/applyColumnAliases'
import ColumnAliasEditor from '@components/reports/ColumnAliasEditor'
import ColumnAliasProvider from '@components/reports/ColumnAliasProvider'
import { getRendererForActionKey } from './reports/registry'
import { trackRunStarted, trackRunCompleted, trackRunFailed, trackResultRendered } from '@/analytics/pendo'
import { reportSurface, resultShape, filterNames } from '@/analytics/reportFacts'
import { errorClass, httpStatusToken } from '@/analytics/errorClass'
import SavedResultBanner from '@components/reports/SavedResultBanner'
import ReportActionBar from '@components/reports/ReportActionBar'
import ReportBand from '@components/reports/ReportBand'
import { ALL_DATES } from '@utils/cbDateSettings'
import { filtersAsArray } from '@utils/reportFilterWire'
// C2 (2026-08-27): the applied-chip display text, its criterion test and its
// removability come from here now, not from a second copy in this file — see the
// note at the pill memo. `dateFilterSummary` and `splitMultiValue` left the two
// imports above with that copy; this module is now their only caller on this
// page's behalf, so removing them here is not a capability loss.
import { filterValueSummary, hasCriterion, isRemovableFilter } from '@components/reports/filterValueSummary'
// E2 (2026-08-27): the ONE list of filter types ReportFilterPanel now renders
// inline in the query card. Imported, never re-declared here — a second copy
// would let the panel and this page's chip row disagree about which criteria are
// already visible on screen.
import { INLINE_FILTER_TYPES } from '@components/reports/inlineFilterTypes'
import ReportErrorBoundary from '@components/reports/ReportErrorBoundary'
import QueueHandoffPanel from '@components/reports/queue/QueueHandoffPanel'
import RunningReportBand from '@components/reports/queue/RunningReportBand'
import { savedRunErrorSentence, isRunOfReport, otherReportRunError } from '@components/reports/queue/resultRetention'
import { QUEUE_HANDOFF_MS, QUEUE_PATH } from '@config/queueConfig'
import { toast } from '@store/toastStore'
import { buildInitialFilters, boolDefaultValue, savedParamsToFilterValues, reconcileStoredCurrentUser } from '@utils/reportFilterDefaults'

/**
 * Generic CB report renderer.
 *
 * Flow:
 *   1. Load definition (columns + filterable columns + filter defaults)
 *   2. Build initial filter state from filterDefault values
 *   3. Auto-run if RunOnOpen != false
 *   4. On Run, kick off /run, poll /process-status, then fetch /data
 *   5. Render the returned Report (ReportTable handles rows/groups/footers)
 */

// CC-49925: a BOOL's seeded value ("1"/"0"). CB core renders a boolean report
// filter as a checkbox whose isBoolChecked only checks for an explicit truthy
// default; anything else is unchecked (→ "False"). Mirror that so the Yes/No
// control, the applied-filter pill, and the run request tell one story.
// buildInitialFilters moved to @utils/reportFilterDefaults so the Queue Report
// modal seeds filters through the SAME logic (it carries CC-49840 / Bug #5 /
// CC-49925 fixes — a second copy would run reports with the wrong filters).

// Layer B (smart search): the entity type-id from the URL (?ef) is matched to this
// report's LOOKUP column, and ?ei/?en pre-set it (as "id:name" — CB's own default
// format) so "run this report for <entity>" opens pre-filtered to that client/matter.
const ENTITY_TYPE_IDS = { matter: 1001, contact: 3 }
const columnTypeIdOf = (col) => {
  const fromCond = col.filterConditions?.[0]?.key
  if (fromCond) return parseInt(fromCond, 10)
  if (col.field) return parseInt(String(col.field).split(':')[0], 10)
  return null
}
const applyEntityPrefill = (initial, definition, { et, ei, en }) => {
  const ef = ENTITY_TYPE_IDS[et]
  if (!ef || !ei) return initial
  const col = (definition?.columns || []).find(
    (c) =>
      c.filterable &&
      (c.filterTypeName === 'LOOKUP' || c.filterTypeName === 'TAGMANAGER') &&
      columnTypeIdOf(c) === ef
  )
  if (!col) return initial
  return { ...initial, [col.columnId]: en ? `${ei}:${en}` : String(ei) }
}

// CC-49334 → CC-49335: persist the SERVER filter-panel selections (the values
// sent to GenerateReportData) so recurring reports are one click. Slice 3 kept
// them per-tab (sessionStorage); CC-49335 upgrades to localStorage so the
// last-used filter set survives logout/login and browser restarts.
// Key scope [SECURITY]: per user (Auth0 sub — unique per tenant+user under
// org-scoped SSO) + per report, so shared machines don't leak one user's
// selections into another's session. Data access itself stays tenant-scoped
// server-side; this is display-preference state only.
// Scope note: the in-grid quick-filters, value-picker selections, and the
// hide-$0 / hide-inactive toggles are component-local and intentionally reset on
// navigation this slice. All access is wrapped — disabled/unavailable storage
// must never break the report.
const filtersStorageKey = (definition, reportId, userKey) =>
  `cb:report-filters:v2:${userKey || 'anon'}:${definition?.actionKey || reportId}`
// Legacy per-tab key (CC-49334) — still read as a fallback so an in-flight
// session keeps its selections across this upgrade, and cleared on reset.
const legacyFiltersStorageKey = (definition, reportId) =>
  `cb:report-filters:${definition?.actionKey || reportId}`
const readStoredFilters = (key, legacyKey) => {
  try {
    const raw = localStorage.getItem(key) || (legacyKey ? sessionStorage.getItem(legacyKey) : null)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}
const writeStoredFilters = (key, values) => {
  try {
    localStorage.setItem(key, JSON.stringify(values))
  } catch {
    /* storage unavailable (private mode / quota) — persistence is best-effort */
  }
}
const clearStoredFilters = (key, legacyKey) => {
  try {
    localStorage.removeItem(key)
    if (legacyKey) sessionStorage.removeItem(legacyKey)
  } catch {
    /* no-op */
  }
}
// CC-49893: order-insensitive value comparison — stored sets can carry the
// same values under a different key order than a freshly built default set.
const stableStringify = (o) =>
  JSON.stringify(Object.keys(o || {}).sort().map((k) => [k, o[k]]))

const REPORT_JOBS_ON = import.meta.env.VITE_REPORT_JOBS === 'true'

/* A fixed id, not a useId(): the Ask trigger in Bar 1 and the panel it controls
   are far apart in the tree, and only ever one of each exists per page, so
   aria-controls can point at a constant instead of threading a generated id
   through the render. */
const AI_PANEL_ID = 'report-ai-panel'

// Find date filters that are still empty.
// DATE columns crash CB's stored proc with "Index was outside the bounds…"
// when a from/to range is empty. SIMPLEDATE columns crash CB's
// `Convert.ToDateTime("")` with "String was not recognized as a valid
// DateTime." when the single-date value is empty. Either way, block the
// run client-side and tell the user which filter to fill.
const emptyDateFilters = (definition, values) =>
  (definition?.columns || []).filter((col) => {
    if (!col.filterable) return false
    const v = values[col.columnId]
    if (col.filterTypeName === 'DATE') {
      if (v === ALL_DATES) return false
      if (!v) return true
      const [from, to] = String(v).split('|')
      return !from && !to
    }
    if (col.filterTypeName === 'SIMPLEDATE') {
      if (v === ALL_DATES) return false
      return !v
    }
    return false
  })

// Filter values → wire format lives in @utils/reportFilterWire (CC-49871: the
// $$cb_all sentinel is dropped for every type except the DROPDOWN family;
// CC-49866: LOOKUP/TAGMANAGER "id:name" values ship as ids-only).

// A report is "flat" when none of its rows are groups — those render through
// ReportDataGrid (sort / show-hide / drag-reorder / persisted layout).
const isFlatReport = (report) =>
  Array.isArray(report?.reportData) &&
  report.reportData.length > 0 &&
  report.reportData.every((r) => !r.isGroup)

// "Grouped" when any row is a group — those render through ReportGroupedGrid,
// which preserves CB's server group headers + subtotals + grand total verbatim
// and layers within-group sort/filter + column show/hide on top (CC-49381).
const isGroupedReport = (report) =>
  Array.isArray(report?.reportData) &&
  report.reportData.some((r) => r.isGroup)

/*
 * A report that ran and legitimately returned NOTHING.
 *
 * It is neither "flat" nor "grouped" by the two predicates above — both need at
 * least one row — so it used to fall through to <ReportTable>, which has no
 * result strip. The whole grid chrome vanished: no row count, no Display View,
 * no Column Filter, no export scope. That is the exact fingerprint that made
 * report 271 look like a broken page rather than an empty result, and it applies
 * to every zero-row report on the generic engine.
 *
 * Deliberately kept OUT of isFlatReport: that predicate also gates the AI
 * summary (grounding a generated summary on zero rows would be worse than not
 * offering it) and `hasGroups` for the export. Only the renderer choice changes.
 */
const isEmptyResult = (report) =>
  Array.isArray(report?.reportData) && report.reportData.length === 0

/**
 * The applied-criteria pills for a given set of filter VALUES.
 *
 * Values that mean "no constraint" ($$cb_all / All) get no pill — a pill IS a
 * criterion (CC-49320) — each is removable except required as-of dates, and
 * filters hidden as irrelevant for this report (CC-49321) never show one.
 *
 * R3 (2026-08-28) — a FUNCTION OF ITS VALUES, not of component state, because it
 * is now called twice: once with the panel's current values (the on-screen
 * chips) and once with the values the displayed result was actually RUN with
 * (the client export header, the print masthead, the AI scope line). One code
 * path for both is the whole point: label resolution, the criterion test and the
 * value derivation cannot differ between the chips and the document, which is
 * the exact class of drift CC-50009 shipped ("RespAtty" on the chip, "Responsible
 * Attorney" in the panel). When nothing has changed since the run the two calls
 * return equal arrays by construction rather than by inspection.
 */
const buildFilterPills = (definition, values) => {
  if (!definition || !values) return []
  const out = []
  for (const col of definition.columns || []) {
    if (!col.filterable || col.filterRelevant === false) continue
    // Bug #7: HIDDEN filters are internal parameters (e.g. Rates by Matter's
    // SortOrder proc arg) — a removable pill for one lets users wipe a value
    // the proc needs. The panel already excludes them; so must the pills.
    if (col.filterTypeName === 'HIDDEN' || col.filterTypeName === 'NONE') continue
    const v = values[col.columnId]
    /*
     * C2 (2026-08-27) — ONE display derivation for a filter value, not two.
     *
     * These two lines used to be a 25-line hand-rolled type chain (DATE /
     * SIMPLEDATE → BOOL → DROPDOWN/RADIO/CHECKBOX → LOOKUP/TAGMANAGER →
     * 40-char fallback) sitting beside a byte-identical copy in
     * `@components/reports/filterValueSummary`, whose own header says it was
     * "lifted verbatim out of ReportView's applied-pill memo so the FILTER
     * PILL's trigger and the applied-criteria chip cannot disagree about the
     * same value". Lifting it left the original in place, so the drift it
     * exists to prevent was still exactly one edit away.
     *
     * Worth the churn because these pills are not only chips: `pillsToLine` over
     * this same array is `documentFiltersSummary`, which feeds the client
     * CSV/XLSX/PDF "Filters —" header, the print masthead and the AI scope line.
     * A divergence here ships inside client documents — which is precisely how
     * CC-50009's "RespAtty" vs "Responsible Attorney" mismatch escaped notice
     * for as long as it did.
     *
     * (R3, 2026-08-28: the line numbers this paragraph used to cite are gone
     * rather than corrected. They were already wrong — this block has moved
     * twice since — and a stale pointer costs more than the lookup it saves.
     * The documents call this function with the RUN's values while the chips
     * call it with the panel's; that is a difference in ARGUMENT, never in
     * derivation, which is what keeps the CC-50009 guarantee intact.)
     *
     * Equivalence was checked type-by-type against filterValueSummary.js
     * before switching, not assumed: same criterion guard (`hasCriterion` is
     * the same four-way test that used to be inline here), same
     * `dateFilterSummary` call, same '1' → Yes/No, same `splitMultiValue` +
     * `filterConditions` label lookup (CC-49897), same "id:name" names-else-
     * count with the same 60-char slice (CC-49866), same 40-char default.
     * The per-type rationale moved with the code — it is commented there.
     */
    if (!hasCriterion(v)) continue
    const display = filterValueSummary(col, v)
    if (display) {
      out.push({
        columnId: col.columnId,
        // CC-50009: the SAME label resolution the filter panel applies
        // (ReportFilterPanel -> resolveFilterLabel). CB's definition can carry
        // two columns for one concept — an abbreviated FILTERABLE one ("RespAtty")
        // and a display-only twin ("Responsible Attorney") — and the panel has
        // preferred the readable twin since this ticket's first pass. The pill
        // path was never updated, so the panel read "RESPONSIBLE ATTORNEY" while
        // the applied-filter chip still read "RespAtty" (QA item 3), which is
        // exactly why the two disagreed between builds.
        //
        // This also fixes the EXPORT header: `documentFiltersSummary` is built
        // from these pills, so the client CSV/Excel/PDF "Filters — …" line
        // carried the abbreviated label too. Nobody had reported that yet.
        label: resolveFilterLabel(col, definition.columns || []),
        value: display,
        // RADIO and BOOL join SIMPLEDATE as non-removable: each always has
        // exactly one value (CC-49861 / CC-49925) — "removing" it would leave
        // an empty value the wire omits while the pill disappears, and an
        // absent filter WIDENS scope on this backend rather than neutralising
        // it. C2 (2026-08-27): the same three-way test, via
        // `isRemovableFilter`, which already carried this rationale verbatim
        // and until now had no caller at all.
        removable: isRemovableFilter(col),
        // E2 (2026-08-27): carried so a RENDER site can ask "is this criterion
        // already on screen as an inline control?" without a second lookup into
        // definition.columns — a lookup that could answer differently from the
        // one three lines up and put a chip's presence out of step with its own
        // label.
        //
        // ADDITIVE, and this function never filters. `documentFiltersSummary`
        // is built from this exact array and feeds the client CSV/XLSX/PDF
        // "Filters —" header, the print masthead and the AI scope line. On a
        // financial report the date range IS the scope of the money: a billing
        // document that no longer states its period is a WRONG document, not a
        // tidier one. Suppression therefore happens at the render site, per
        // site, and only there.
        filterTypeName: col.filterTypeName,
      })
    }
  }
  return out
}

// The one-line rendering of those pills, used for every document header.
const pillsToLine = (pills) => pills.map((p) => `${p.label}: ${p.value}`).join(' · ')

/*
 * Spec 176 — the scope a set of panel values ASKS CB for: the wire filters, in a fixed
 * order. Two value objects that send CB the same filters are the same scope, however
 * their keys were inserted; that is the only sense in which "the figures match the
 * filters" means anything on this page.
 */
const wireScope = (values, definition) => (
  filtersAsArray(values || {}, definition?.columns, definition?.reportType)
    .map((f) => JSON.stringify([String(f.columnId), String(f.value)]))
    .sort()
    .join(',')
)

const ReportViewInner = ({ backTo = REPORTS_LIST_PATH } = {}) => {
  const { reportId: reportIdParam } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const reportId = parseInt(reportIdParam, 10)
  // CC-49903: Back returns to where the user came from (e.g. the selected
  // Reports category), falling back to the plain catalog on a cold load.
  const goBack = useBackNav(backTo)
  // CC-49981: print/PDF mastheads carry the firm's real name (system settings),
  // not the tenant subdomain. Resolved once here and passed to ReportExport.
  const firmName = useFirmName()
  const aiMode = useAiMode() // 'off' | 'summary' | 'chat'
  // CC-49335: user identity scopes the persisted-filters key (see above).
  const { user, isLoading: authLoading } = useAuth0()
  const userKey = user?.sub

  // 1. Load skeleton (columns, filters, etc.)
  const definitionQuery = useQuery({
    queryKey: ['report-def', reportId],
    queryFn: () => reportService.getDefinition(reportId),
    enabled: Number.isFinite(reportId),
  })
  const { data: rawDefinition, error: defError, refetch: refetchDefinition } = definitionQuery
  // Three outcomes, not two — see `@api/v1/readState`. `isLoading` alone cannot
  // tell "still fetching" from "never started", and the difference here is
  // "Loading report…" versus a false "Report not found".
  const defState = useReadState(`report-def:${reportId}`, definitionQuery)

  // 1a. Per-firm column names (spec 061). ONE injection point, here, where the
  // column list enters the page: the grid header, filter-bar labels, Group By,
  // tooltips, aria-labels, saved views and the client exports all read
  // `col.label` off these same objects, so they inherit the firm's word without
  // eight separate wiring jobs (spec 073 §3). `columnId` is untouched — it is
  // the wire key and every saved view's storage key.
  const { aliases, canManage: canManageAliases } = useColumnAliases(reportId)
  const definition = useMemo(() => aliasDefinition(rawDefinition, aliases), [rawDefinition, aliases])
  const [renamingColumns, setRenamingColumns] = useState(false)

  // 1b. Whether report has a chart in CB
  const { data: hasChart = false } = useQuery({
    queryKey: ['report-has-chart', reportId],
    queryFn: () => reportService.hasChart(reportId),
    enabled: Number.isFinite(reportId),
  })

  useDocumentTitle(definition?.name || 'Report')

  // 2. Filter state — keyed by columnId
  const [filterValues, setFilterValues] = useState({})
  // CC-49893: true when this load restored a persisted filter set that differs
  // from the report's defaults — drives the "saved filters applied" banner.
  const [savedFiltersNotice, setSavedFiltersNotice] = useState(false)
  /*
   * E4 (2026-08-27) — "Reset filters" restores the defaults but does NOT re-run,
   * so the grid underneath is showing money computed from the filters that were
   * just thrown away. This flag is that divergence, and nothing else.
   *
   * Why a flag and not a re-run: Jordan's call, 2026-08-27. Re-running would fire
   * a report execution the user did not ask for, and MAX_RUN_MS is six minutes
   * because it mirrors CB's 300s SQL CommandTimeout — an unrequested run on a
   * heavy report is a real cost, not a free refresh. Reset stays cheap; the GRID
   * says it is stale.
   *
   * Why it did not matter until now: before E2/E3 the date lived behind a closed
   * popover, so a user could not see the panel and the grid disagree. The dates
   * are permanently on screen now — the range visibly snaps back to the report's
   * default while the figures below it do not move. That is a visible
   * wrong-number pattern on a financial grid, which is the whole reason this
   * exists.
   *
   * SCOPE: set by the Reset path only. An ordinary filter edit does NOT set it —
   * marking the grid stale on every keystroke across 140 reports is a much larger
   * behavioural change and was not what was asked.
   */
  const [resetSinceRun, setResetSinceRun] = useState(false)

  // 3. Run state
  /*
   * Spec 131 — only the NEWEST run may write the screen.
   *
   * Every run on this page (Run/Refresh, the open auto-run, a chip removal, an
   * applied view, the saved-filters band's "Reset to defaults", "Run in the
   * background now", a `?jobId=` reopen and its "Run again, same filters") takes a
   * ticket when it STARTS, and every write it makes after an await — the job id,
   * the poll progress, the rows, the error, and turning the spinner off — is made
   * only while that ticket is still the latest. Before this, those writes landed in
   * the order the requests ANSWERED: cb-api answers a repeated combination from its
   * in-flight / 5-minute ready dedupe at once, so the newer run was often the
   * fastest and the older one landed last and replaced it, under a filter panel
   * that described the newer run. On QA the chevron "Run in the background now"
   * reaches that on any of the 119 generic reports; on prod, "Reset to defaults"
   * and an applied view do.
   *
   * Commits are gated; nothing is aborted (spec 131 decision 5). A superseded job
   * is polled to its terminal state exactly as before — cb-api advances a job one
   * step per poll, so a job whose polls stop sits in `running` against the
   * firm-wide in-flight cap until the cron reaches it.
   */
  const { begin: beginRun, invalidate: retireRun } = useRunTicket()
  const [runError, setRunError] = useState(null)
  /*
   * Spec 176 (CC-54206) — the page stopped following a run WITHOUT the run failing:
   * 'running' when its status reads kept failing in transit past the job's lifetime,
   * 'finished' when the server said `ready` but the result read did not answer. Never
   * a red error and never "took too long": the run may well be fine, and the Queue
   * is where its result is. Cleared wherever `runError` is.
   */
  const [runNotice, setRunNotice] = useState(null)
  /*
   * Spec 176 — the filter values of the run (or reopen) the user most recently ASKED
   * for, whether or not it produced the rows on screen. When those rows were run with
   * a different scope, the page says so above them (`report-superseded`): the
   * previous run's figures stay visible after a newer run fails, is handed off to the
   * queue, is dismissed or stops reporting, and on QA 2026-10-13 that put a scoped
   * 208,033.72 under a panel reading "Timekeeper: Any" with nothing to say so.
   */
  const [askedFilters, setAskedFilters] = useState(null)
  const [progress, setProgress] = useState(null) // { percent, message }
  /*
   * `filterValues` is part of the RESULT, not a separate piece of state.
   *
   * R3 (2026-08-28): the three provenance surfaces below — the client export
   * header, the print masthead and the AI scope line — used to describe the
   * CURRENT panel values. Those two things come apart the moment the panel
   * changes without a run (Reset filters, or simply editing a filter and not
   * clicking Run), and the document then STATES one scope while CARRYING the
   * figures of another. On a billing or A/R report that is a wrong document.
   *
   * Storing the filters inside `runResult` rather than in a parallel state is
   * the point: they are written by the same `setRunResult` call that publishes
   * the report, from both producers (the run at :516 and the `?jobId=` reopen
   * at :621), so a result can never be on screen under filters that belong to a
   * different one. CC-53051 is the precedent for why that matters — a fresh
   * 12-row run once landed under a banner belonging to a 52-row run.
   *
   * `seq` (spec 131) is the ticket of the run that produced this result — its
   * identity. The AI panel is keyed on it, so a result swapped in under a mounted
   * panel (a handed-off run landing) starts a fresh summary or chat instead of
   * keeping one written about the previous rows.
   */
  const [runResult, setRunResult] = useState(null) // { report, processId, filterValues, seq }
  // Declared HERE, beside the other hooks, rather than at the point of use further
  // down: that point sits AFTER this component's early returns, so a useMemo there
  // is a conditionally-called hook. `npm run build` accepted it happily — only
  // lint caught it, which is exactly the "builds pass even with runtime errors that
  // only surface at run time / conditionally-called hooks" case CLAUDE.md calls out.
  const aliasedRunReport = useMemo(
    () => aliasDefinition(runResult?.report, aliases),
    [runResult?.report, aliases]
  )
  const [savedAsOf, setSavedAsOf] = useState(null) // ms — set when reopened from the tray (?jobId)
  // CC-49354 #3: whether the active grid currently hides rows (per-column
  // filters / toggles) — drives the export control's full-dataset warning.
  const [gridFiltered, setGridFiltered] = useState(false)
  const [running, setRunning] = useState(false)
  // Long-run ticker: forces a re-render each second so the elapsed-time hint stays
  // current even when CB reports no progress. runStartRef is stamped at run start.
  const runStartRef = useRef(null)
  const [, setRunTick] = useState(0)
  useEffect(() => {
    if (!running) return undefined
    const t = setInterval(() => setRunTick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [running])
  // Queue handoff (spec 056): the id of the job backing the current run, and
  // whether the user chose "Run in the background now" (which hands off at once
  // rather than waiting out QUEUE_HANDOFF_MS).
  const [activeJobId, setActiveJobId] = useState(null)
  const [forceHandoff, setForceHandoff] = useState(false)
  // null | 'once' | 'repeat' — which scheduling flow the split button opened.
  const [scheduleWhen, setScheduleWhen] = useState(null)
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false)
  const [notifyArmed, setNotifyArmed] = useState(false)
  const invalidateSchedules = useInvalidateSchedules()

  // The active grid publishes a getter for its current view here (CC-49370),
  // so the export control can build a client "export what you see".
  const exportViewRef = useRef(null)

  // CC-49732: the active grid publishes its page state (quick-filters + toggles)
  // here for the Views menu to capture; `appliedGridStateRef` carries a restored
  // view's grid state into the remounted grid.
  const pageStateRef = useRef(null)
  const appliedGridStateRef = useRef(null)
  // CC-49844: the persisted "Data only" choice, so the print masthead honors it
  // exactly as the CSV/Excel/PDF writers do.
  const exportDataOnly = !!usePreferencesStore().getExportDataOnly?.()

  // CC-49458: user-selectable Group By — '' = the report's own layout (server
  // grouping for grouped reports, flat otherwise); a columnId regroups
  // client-side via groupFlatReport (SUM-only synthetic subtotals).
  const [groupBy, setGroupBy] = useState('')
  // CC-49336: bumped when a named view is applied, so the grid remounts and
  // re-reads the layout the view wrote into preferencesStore.
  const [viewNonce, setViewNonce] = useState(0)

  // CC-49862 (Jordan, 7/22): the filter panel no longer AUTO-collapses after a
  // run — it stays open so the filters are always visible at the top of the
  // page. The manual "Hide filters ▲" control still lets a user collapse it to
  // the one-line Applied-chips summary + "Edit filters" on demand.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false)
  // AI panel collapses by default (reclaim space + avoid an AI call until asked);
  // a slim button expands it on demand.
  const [aiCollapsed, setAiCollapsed] = useState(true)
  // A new report re-shows the filters (and re-collapses AI); a manual collapse
  // is per-report and resets here.
  // E4: a different report cannot inherit the previous one's stale-since-reset
  // state — there is no result on screen yet for it to be stale against.
  /*
   * Spec 131 — a report CHANGE also retires the previous report's run and clears
   * its result. This page is one component instance across reports (the route is
   * not keyed by id), so without this report A's run, still in flight, landed on
   * report B's page with B's filter panel above it — and when B does not run on
   * open, A's last result stayed on screen under B's title with no run at all.
   *
   * Three placement rules, each load-bearing:
   *
   *   - HERE, in this effect, declared ABOVE the reopen and auto-run effects.
   *     Effects run in declaration order, so on a warm-cache A→B navigation
   *     (B's definition already cached, so effect 4 starts B's open auto-run in
   *     the same commit) the retire happens FIRST and B's run takes the next
   *     ticket. Declared below them, it would retire B's own run instead and
   *     leave A's rows on B's page with no spinner.
   *   - Never on mount, nor on StrictMode's simulated remount: the ref only
   *     differs from `reportId` on a real change, so the first run of the page
   *     is never retired by its own mount.
   *   - It clears the result, the saved-snapshot banner and the error along with
   *     the ticket. Retiring alone would stop A's in-flight run from landing but
   *     leave A's LAST result standing.
   */
  const shownReportIdRef = useRef(reportId)
  useEffect(() => {
    setFiltersCollapsed(false); setAiCollapsed(true); setResetSinceRun(false)
    if (shownReportIdRef.current === reportId) return
    shownReportIdRef.current = reportId
    retireRun()
    setRunResult(null)
    setSavedAsOf(null)
    setRunError(null)
    setRunNotice(null)
    setAskedFilters(null)
    setRunning(false)
    setProgress(null)
    setActiveJobId(null)
    setForceHandoff(false)
  }, [reportId, retireRun])

  /*
   * `trigger` (spec 094) names what CAUSED this run. It is analytics-only and
   * changes no behaviour, but it is load-bearing for the question the beta is
   * actually asking. Most runs are NOT a Run click: the effect below auto-runs
   * on open whenever `definition.runOnOpen !== false`, which is most reports.
   * Counting runs without it would make "most-run report" a restatement of
   * "most-visited page" — which the free Pendo Page tag already gives us — and
   * would answer the plan's item 4 ("distinguish ran from visited") backwards.
   */
  const runReportWith = useCallback(async (values, { background = false, deliver, trigger = 'user_run' } = {}) => {
    if (!definition) return
    // Spec 176: these are the figures the user now asked for — including a run refused
    // below for an empty date, which leaves the previous rows under a panel that no
    // longer describes them just as a failed run does.
    setAskedFilters(values)
    setRunNotice(null)
    // Validate: empty DATE filters cause CB stored procs to throw
    // "Index was outside the bounds of the array".
    const missingDates = emptyDateFilters(definition, values)
    if (missingDates.length > 0) {
      const labels = missingDates.map((c) => c.label).join(', ')
      // SIMPLEDATE is a single "as of" anchor — it has no range and no
      // "Clear / All Dates" affordance, so don't tell the user to pick a range.
      const allSingle = missingDates.every((c) => c.filterTypeName === 'SIMPLEDATE')
      // Spec 131: a refusal retires whatever is still in flight — reachable while a
      // run is out via "Run in the background now" or an applied view. Without
      // this, the earlier run landed underneath the refusal's message, so the page
      // said "set a date" above figures for a date range the panel no longer
      // shows. The spinner goes with it: nothing else will ever turn it off.
      retireRun()
      setRunning(false)
      setProgress(null)
      setRunError(
        allSingle
          ? `Required date${missingDates.length > 1 ? 's' : ''}: ${labels}. Set a date to run this report.`
          : `Required date filter${missingDates.length > 1 ? 's' : ''}: ${labels}. Pick a range or click "Clear / All Dates".`
      )
      return
    }
    // Synchronously, before the first await: a caller that started this run from
    // inside another run's flow (the reopen's "Run again, same filters") relies on
    // its own ticket being superseded by the time it next checks.
    const t = beginRun()
    setRunning(true)
    // Local copy as well as the ref: a second run overwrites runStartRef, which
    // would then report the wrong elapsed time for THIS run's analytics.
    const startedAt = Date.now()
    runStartRef.current = startedAt
    setRunError(null)
    // E4: cleared HERE — below the empty-date guard, so a run that was refused
    // before it started leaves the grid correctly marked stale. Every run path
    // (Run/Refresh, the chip row, an applied view, the saved-filters band's own
    // reset, the on-open auto-run) passes through this one line, so there is no
    // second place that has to remember to clear it.
    setResetSinceRun(false)
    // NOT `setSavedAsOf(null)` here any more (spec 131 decision 3). The banner
    // describes the rows on screen, and at run START those are still the saved
    // snapshot's: a run that hands off (the snapshot comes back — showResult) or
    // fails leaves them there. Clearing it here stripped the provenance off a
    // saved result that was still showing, so it read as a live one. It is
    // cleared where the fresh rows are committed, below.
    setProgress({ percent: 0, message: 'Starting…' })
    // "Run in the background now" hands off immediately — the user already knows
    // this report is heavy and shouldn't wait out the threshold to be told.
    setForceHandoff(background)
    setActiveJobId(null)
    setNotifyArmed(false)
    /*
     * The EXECUTION path, which is not the same thing as `background`.
     *
     * The plan's reference wiring sets runMode from `background`, but the job
     * branch below is entered whenever VITE_REPORT_JOBS is on, whatever
     * `background` is — `background` only decides whether the user WAITS
     * (forceHandoff) and whether delivery is armed. So it is reported
     * separately, as `handoff`.
     */
    const asJob = import.meta.env.VITE_REPORT_JOBS === 'true'
    // Assigned inside the try (it needs the wire filters) but read in the catch.
    let runMeta = null
    try {
      const filters = filtersAsArray(values, definition?.columns, definition?.reportType)
      runMeta = {
        reportId,
        reportName: definition?.name,
        // Registry membership, NOT actionKey truthiness — the same predicate
        // that picks the renderer, so the reported surface cannot disagree
        // with what was drawn.
        reportSurface: reportSurface(definition?.actionKey),
        actionKey: definition?.actionKey || '',
        reportType: definition?.reportType,
        runMode: asJob ? 'job' : 'sync',
        handoff: background,
        trigger,
        filterCount: filters.length,
        // NAMES ONLY, via resolveFilterLabel. Never a filter value: those are
        // client, matter and timekeeper names and firm date ranges.
        filtersUsed: filterNames(filters, definition?.columns),
      }
      trackRunStarted(runMeta)
      let result
      if (asJob) {
        // Async job path (spec 055): the report runs as a background job decoupled
        // from this request, so a slow report never hits the edge timeout. We poll
        // for progress and render the fresh result via the existing enriched /data.
        const created = await reportJobService.create({
          kind: 'generic', reportId, filters,
          // Delivery only makes sense for a hand-off (background) run — the cron
          // emails it when ready; a foreground run is already on screen.
          ...(background && deliver?.deliverEmail ? { deliverEmail: true, deliverFormat: deliver.deliverFormat } : {}),
        })
        // Publish the job id so the handoff panel can name it and the queue can
        // pick it up if the user leaves. Only while this run is the latest: the
        // band's Cancel ("Stop waiting") cancels `activeJobId`, and a superseded
        // run's create answering last used to point it at the OLD job — so the
        // cancel went to a run nobody was waiting on while the current one ran on.
        if (t.isCurrent()) setActiveJobId(created.jobId)
        // Gate on the STATE, not on `reused`: a deduped job may be joined while it is
        // still running/queued, and the server now reports that real state. Keying off
        // `reused` sent those into the result fetch, which 409s ("Not ready") instead
        // of polling the run forward.
        if (created.state === 'ready') {
          // Load through the SAME enriched /data path the poll branch uses. The R2
          // blob is CB's RAW PascalCase report (`ReportData`), but this view reads
          // camelCase (`report.reportData`), so feeding the blob straight in rendered
          // "No data" on every reused run while the rows sat intact in R2. Passing
          // the jobId as `processId` also broke the server export (CB expects a
          // processId). Fall back to the blob only if the job carries no processId.
          const pid = created.processId
          // Superseded while the create was out: the job is already terminal
          // (`ready`), so there is nothing to poll forward and nothing of ours to
          // show — skip reading rows that could only be dropped.
          if (!t.isCurrent()) return
          try {
            if (pid) {
              // Pass the jobId so this reads OUR stored copy of the run rather than
              // asking CB for a processId file it may already have discarded
              // (CC-50284). This is the reuse branch, so the run can be up to
              // `readyReuseWindow` old — precisely the case CB stops serving first.
              const report = await reportService.getData(reportId, pid, { jobId: created.jobId })
              result = { report, processId: pid }
            } else {
              const rep = await reportJobService.result(created.jobId)
              result = { report: rep?.data ?? rep, processId: created.jobId }
            }
          } catch {
            // The dedupe reported "ready" but the result wasn't fetchable yet — a
            // 409 "Not ready" (joined a run mid-flight) or a 410 (expired). Don't
            // surface the raw status code; fall through to the poll loop below,
            // which carries the run forward and shows real progress/errors.
            result = null
          }
        }
        if (!result) {
          // eslint-disable-next-line no-constant-condition
          for (;;) {
            await new Promise((r) => setTimeout(r, RUN_POLL_MS))
            /*
             * Spec 176 (CC-54206): a status read that fails in transit — axios's 30s
             * timeout, a dropped connection, a 5xx — is not the run failing. It used to
             * throw straight into the catch below, which said "This report took too long
             * to return. Narrow the date range…" about a job that finished seconds later
             * (QA 2026-10-13, report 121, two runs of three). `null` = read again; past
             * the job's lifetime `readRunStatus` throws a "still running" error instead.
             */
            const st = await readRunStatus(created.jobId, { startedAt })
            if (!st) continue
            /*
             * A superseded run keeps polling — deliberately (spec 131 decision 5):
             * cb-api advances a job one step PER POLL, so abandoning the loop would
             * park the job in `running`, counted against the firm-wide in-flight
             * cap until the cron reaches it. It just stops writing: its progress
             * is not the progress of the run on screen.
             */
            if (t.isCurrent()) setProgress({ percent: st.percent ?? 0, message: st.message || '' })
            if (st.state === 'failed') throw new Error(st.error || 'The report failed.')
            // `cancelled` (the band's Cancel, or a cancel from the queue) and
            // `expired` end a job with no result, exactly like `failed`. This loop
            // used to exit on `ready`/`failed` only, so a job that ended either
            // way was polled every 1.5s for as long as the page stayed open — and
            // now that a superseded run is polled to its end rather than dropped,
            // a missing terminal would keep that loop alive forever.
            if (ENDED_WITHOUT_RESULT.has(st.state)) throw new Error(ENDED_WITHOUT_RESULT.get(st.state))
            if (st.state === 'ready') {
              // Polled to its end; a superseded run has nothing to fetch.
              if (!t.isCurrent()) return
              // Spec 176: the server said ready, so a read of the result that does not
              // answer is "finished, open it from the Queue" — never "took too long".
              const report = await readFinishedResult(
                () => reportService.getData(reportId, st.processId, { jobId: created.jobId })
              )
              result = { report, processId: st.processId }
              break
            }
          }
        }
      } else {
        // The PROD path (VITE_REPORT_JOBS is off in deploy-prod.yml): same gate.
        // No `signal` is passed although runAndWait accepts one — spec 131 gates
        // commits and aborts nothing, on every path.
        result = await reportService.runAndWait(reportId, filters, {
          onProgress: (percent, message) => { if (t.isCurrent()) setProgress({ percent, message }) },
        })
      }
      // THE gate. A run superseded while it was out — by a newer run, a refusal,
      // Cancel, Dismiss, or a report change — lands here and is dropped: its rows,
      // its "rendered" analytics and its turn at the spinner all belong to
      // whichever run the page is showing now.
      if (!t.isCurrent()) return
      // The grid's published view describes the rows it is showing NOW — the
      // previous result's. Withdraw it with those rows: the AI panel (keyed on
      // `seq`, below) remounts in this commit and its effect runs BEFORE the
      // grid's republish, so it would otherwise ground a "new" summary on the
      // old figures. Null makes it wait for the grid instead (useAutoStart's
      // retry), which is the path it already takes on a first open.
      exportViewRef.current = null
      // `values` — the filters THIS run was made with — travel with the result.
      // Not `filterValues`: this callback does not close over it (it is not a
      // dependency, deliberately), and the panel may well have moved on while
      // the run was in flight.
      setRunResult({ ...result, filterValues: values, seq: t.seq })
      // Decision 3: the saved-snapshot banner comes down when LIVE rows replace
      // the snapshot — here, in the same commit — not when the run started.
      setSavedAsOf(null)
      /*
       * Terminal event. cb-api's job runner emits `Report Run Completed` for
       * every job it finishes, including one the user watches in-tab, so
       * emitting it here as well would double-count "reports per tenant" — a
       * beta go/no-go number. The server owns runMode job|scheduled; the
       * client owns sync. On the job path the browser instead reports what it
       * RENDERED, which carries `resultShape` (the runner cannot know it) and
       * is a genuinely different fact: a job can finish with no tab open.
       */
      const terminal = {
        ...runMeta,
        resultShape: resultShape(result?.report),
        rowCount: result?.report?.recordCount,
        durationMs: Date.now() - startedAt,
      }
      if (asJob) trackResultRendered({ ...terminal, outcome: 'rendered' })
      else trackRunCompleted(terminal)
    } catch (err) {
      // A superseded run's failure is not this screen's failure: a red pane over
      // the newer run's figures (or over its spinner) would be false. It is not
      // reported to analytics either — the user was never shown it.
      if (!t.isCurrent()) return
      // Spec 176: a run the page stopped FOLLOWING is not a run that failed — a notice
      // that points to the Queue, not the red band and not runFailureMessage's
      // timeout advice (which is what this used to say about a finished run).
      const lost = isJobStatusLost(err)
      if (lost) setRunNotice(err.finished ? 'finished' : 'running')
      else setRunError(runFailureMessage(err))
      /*
       * errorClass is an ENUM derived from the error's SHAPE. The message never
       * leaves the browser: CB's terminal strings have carried a server
       * filesystem path and a tenant directory name (CC-49993), and its 5xx
       * bodies carry SQL exception text.
       */
      // A lost run is classified by the transport failure that lost it.
      const shape = lost ? (err.cause ?? err) : err
      const failure = {
        ...runMeta,
        durationMs: Date.now() - startedAt,
        errorClass: errorClass(shape),
        httpStatus: httpStatusToken(shape),
      }
      // On the job path this means the USER did not get the result; the job
      // itself may well be `ready` server-side. That divergence is the signal.
      if (asJob) trackResultRendered({ ...failure, outcome: 'failed' })
      else trackRunFailed(failure)
    } finally {
      // Only the latest run may turn the spinner off. A superseded run finishing
      // first used to flip the control back to "Refresh" and draw the grid while
      // the run the panel describes was still out — and then that grid changed
      // underneath the reader with no spinner at all.
      if (t.isCurrent()) {
        setRunning(false)
        setProgress(null)
      }
    }
  }, [definition, reportId, beginRun, retireRun])

  const runReport = useCallback(() => runReportWith(filterValues), [runReportWith, filterValues])

  // Tray "View report" (?jobId=…) → load a finished job's result through the enriched
  // /data path, naming the job so it is served from OUR stored copy (CC-50284).
  //
  // The comment here used to say the result "survives ~24h keyed only by processId".
  // That was the bug: our retention is a WEEK, CB's processId file is much shorter,
  // and reopening anything past CB's window was a hard 500 with a perfectly good
  // result sitting in R2. Retention is now bounded by OUR result TTL, not CB's.
  const jobIdParam = searchParams.get('jobId')
  // CC-53051: the second signal the queue needs. "View report", "Open report & edit
  // filters" and "Run again, same filters" all navigated to the SAME `?jobId=` URL, so
  // nothing downstream could tell them apart — the fix cannot key off `?jobId=` alone.
  const rerunParam = searchParams.get('rerun') === '1'
  useEffect(() => {
    if (!jobIdParam || import.meta.env.VITE_REPORT_JOBS !== 'true') return undefined
    // Specialized renderers own their own ?jobId load (they read the saved result
    // directly and show an "as of" banner). Wait for the definition, then skip.
    if (!definition) return undefined
    if (getRendererForActionKey(definition.actionKey)) return undefined
    let cancelled = false
    /*
     * Spec 131 — a reopen is a run as far as the screen is concerned, so it takes a
     * ticket too, and that fixes two orderings at once:
     *
     *   - A LIVE run still in flight when the user opens a saved result of the SAME
     *     report from the tray (this component is not remounted by `?jobId=`)
     *     used to land after the snapshot and replace it — live figures under a
     *     "Saved result — as of" banner, the CC-53051 shape again. The reopen's
     *     ticket supersedes it.
     *   - "Run again, same filters" starts a real run from inside this flow and
     *     returns. This effect's `finally` then turned the spinner OFF while that
     *     run was in flight (measured: no "Running…", Run enabled). The rerun's
     *     own ticket supersedes this one synchronously, so the `finally` below no
     *     longer touches state that now belongs to the rerun.
     */
    const t = beginRun()
    const live = () => !cancelled && t.isCurrent()
    ;(async () => {
      setRunning(true)
      runStartRef.current = Date.now()
      setRunError(null)
      setRunNotice(null)
      setProgress({ percent: 100, message: 'Loading saved result…' })
      try {
        const st = await reportJobService.status(jobIdParam)
        // Spec 168 — a run of ANOTHER report is refused before anything of it reaches
        // this page: not its filters (seeded below), not its rows, not its error text,
        // and not a "Run again" with its filters. Measured on QA 2026-10-12: cb-api
        // served a report-49 run, byte for byte, as `/reports/190/data?jobId=`, and an
        // Aged A/R run opened here showed "Request failed with status code 400".
        // Checked FIRST, so a failed run of another report is refused as such too.
        if (!isRunOfReport(st, reportId)) throw otherReportRunError()
        // A reopened job that FAILED (or isn't ready) has no result to fetch —
        // show its real error rather than fetching /data (500) or /result (409).
        if (st.state === 'failed') throw new Error(st.error || 'This report run failed.')
        if (st.state !== 'ready') throw new Error('This report run hasn\'t finished yet.')
        // A REOPEN is the strongest case for our own copy (CC-50284): the banner
        // promises "saved as of <time>", the run can be a week old, and CB's
        // processId file is long gone by then. Naming the job means the snapshot is
        // read from R2 and CB is never asked.
        // CC-53051 — seed the panel from the JOB's own filters, so what the user sees
        // is what actually ran. Available for generic jobs as of CC-53048, which reads
        // them out of the filter array already stored in `params_json` — so this works
        // for jobs created before that change too, with no migration.
        const jobFilters = savedParamsToFilterValues(definition, st?.savedParams)
        // Spec 176: the reopen ASKS for the saved run's figures, so its filters are what
        // the rows on screen are measured against — the same object the commit below stores.
        const reopened = jobFilters ? { ...filterValues, ...jobFilters } : filterValues
        if (live() && jobFilters) {
          setFilterValues((prev) => ({ ...prev, ...jobFilters }))
          setAskedFilters(reopened)
        }

        if (rerunParam) {
          // "Run again, same filters" — honour the label. A REAL new run, using the
          // job's filters, and no "saved as of" banner because these figures are fresh.
          //
          // Before this, all three affordances navigated to the same bare `?jobId=` URL
          // and the restore effect below then auto-ran with the user's PERSISTED filters.
          // Reproduced live: a run of 2019-02-23|2019-03-02 (52 rows) re-ran as
          // 2020-02-23|2020-03-24 (12 rows), and because the fresh run settles after the
          // R2 snapshot read, its 12 rows landed in the grid UNDER a banner reading
          // "Saved result — as of …" that belonged to the 52-row run. Financial figures
          // presented under someone else's provenance label.
          //
          // Spec 131: no `setSavedAsOf(null)` here. If a snapshot is on screen it
          // stays labelled as one until the rerun's fresh rows commit (decision 3) —
          // runReportWith clears the banner in that same commit.
          if (live()) {
            runReportWith(jobFilters || filterValues, { trigger: 'rerun_same_filters' })
          }
          return
        }

        const report = await reportService.getData(reportId, st.processId, { jobId: jobIdParam })
        if (live()) {
          // A reopen is a run too, as far as provenance goes: the figures below
          // belong to the JOB's filters, which is exactly what was seeded into
          // the panel five lines up. Same merge, so the masthead and the panel
          // agree on a reopen and keep agreeing if the user then edits.
          exportViewRef.current = null // see the run's commit: the old grid's view goes with its rows
          setRunResult({
            report,
            processId: st.processId,
            filterValues: reopened,
            seq: t.seq,
          })
          setAskedFilters(reopened)
          setSavedAsOf(st?.createdAt || null) // show the "as of" snapshot banner
        }
      } catch (err) {
        // Spec 152 — cb-api answers 410 for a named run whose stored result the bucket
        // has deleted. Say that in words; axios's own message for it is "Request failed
        // with status code 410", which is all this used to show (and 500 before that).
        // Spec 168: through the SAME mapping the 21 specialized pages use, so a run of
        // another report — this guard's refusal, or cb-api's 409 on `/data` — reads one
        // plain sentence here too. 410 maps to the sentence it always did.
        if (live()) setRunError(savedRunErrorSentence(err) || err?.message || 'That report result is no longer available. Please run it again.')
      } finally {
        if (live()) { setRunning(false); setProgress(null) }
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobIdParam, rerunParam, reportId, definition])

  // 4. When definition arrives: compute initial filter values, populate state,
  //    and (if RunOnOpen) auto-run using those values *synchronously* — we
  //    can't rely on the state update being visible in the same tick.
  //    Skip the auto-run when a specialized renderer is registered — those
  //    components handle their own data fetching and the generic engine call
  //    here would either return 500 (specialized reports often aren't
  //    materializable through GenerateReportData) or duplicate the request.
  useEffect(() => {
    // Bug #8: don't restore (or later persist) until Auth0 has hydrated `user`.
    // On a cold start the definition can win the race, making the read hit the
    // anon key, fall back to defaults, and the write effect then OVERWRITES the
    // user's saved filters with those defaults.
    if (!definition || authLoading) return
    // CC-53051 — a `?jobId=` reopen is owned by the effect above, which seeds the panel
    // from the JOB's filters. This effect must not participate at all.
    //
    // It has no `jobIdParam` guard today, and `searchParams` is in its dep array, so on a
    // reopen it ALSO ran: it restored the user's persisted filters over the job's and
    // then, because `definition.runOnOpen !== false` for most reports, fired
    // `runReportWith(initial)` — a whole extra CB run, billed and queued, at the wrong
    // scope. Verified live on the deployed host, twice.
    //
    // Returning here leaves the reopen path with exactly one owner. A normal (no-jobId)
    // open is untouched.
    if (jobIdParam && import.meta.env.VITE_REPORT_JOBS === 'true') return
    // CC-49335: restore the user's persisted filters if present, else CB defaults.
    const stored = readStoredFilters(
      filtersStorageKey(definition, reportId, userKey),
      legacyFiltersStorageKey(definition, reportId)
    )
    const defaults = buildInitialFilters(definition)
    // Spec 164: a stored raw `$$cb_userid` takes the default cb-api now resolves it to.
    const base = stored && Object.keys(stored).length > 0 ? reconcileStoredCurrentUser(stored, defaults, definition) : defaults
    // CC-49925: a BOOL is always applied (Yes/No, no "Both"). Coerce any
    // missing/empty BOOL — e.g. a filter set persisted by the old tri-state
    // build — to its default so the control never shows a value the wire omits.
    for (const col of definition.columns || []) {
      if (col.filterable && col.filterTypeName === 'BOOL' && base[col.columnId] !== '1' && base[col.columnId] !== '0') {
        base[col.columnId] = boolDefaultValue(col)
      }
    }
    // CC-49893: persistence (CC-49335) is working as designed, but a saved
    // Client/Matter selection silently narrowing a report reads as MISSING
    // DATA (QA read a stale trust-balance filter as "this client has no trust
    // balance"). Product call (2026-07-30): keep persistence everywhere and
    // make the restored state unmissable instead — banner over the panel.
    // Only announce a restore that actually differs from the defaults
    // (compared AFTER the BOOL coercion so normalized old sets stay quiet).
    setSavedFiltersNotice(stableStringify(base) !== stableStringify(defaults))
    // Smart-search entity prefill (?ef/ei/en) wins over stored/default for its column.
    const prefill = { et: searchParams.get('et'), ei: searchParams.get('ei'), en: searchParams.get('en') }
    const initial = applyEntityPrefill(base, definition, prefill)
    setFilterValues(initial)
    const hasSpecialized = !!getRendererForActionKey(definition.actionKey)
    // Force a run when prefilled (the user explicitly asked for this entity's data).
    // CC-49862: keep the filter panel open on auto-run (no auto-collapse).
    if (!hasSpecialized && (definition.runOnOpen !== false || !!prefill.ei)) {
      runReportWith(initial, { trigger: 'open_auto' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition?.reportId, searchParams, authLoading])

  // CC-49335: persist filter selections per user + report (cross-session), so
  // they survive in-report search, navigation, and logout/login.
  useEffect(() => {
    if (!definition || authLoading) return // bug #8: never write under the wrong key
    // CC-53051: do NOT persist on a `?jobId=` reopen. The panel is showing the JOB's
    // filters, not a selection the user made, and writing them would silently replace
    // their own saved set as a side effect of looking at an old run. Opening a snapshot
    // is a read.
    if (jobIdParam) return
    writeStoredFilters(filtersStorageKey(definition, reportId, userKey), filterValues)
  }, [filterValues, definition, reportId, userKey, authLoading, jobIdParam])

  const onFilterChange = useCallback((columnId, value) => {
    setFilterValues((prev) => ({ ...prev, [columnId]: value }))
  }, [])

  const onResetFilters = useCallback(() => {
    if (!definition) return
    // Reset clears the persisted selections so the report returns to its defaults.
    clearStoredFilters(
      filtersStorageKey(definition, reportId, userKey),
      legacyFiltersStorageKey(definition, reportId)
    )
    const defaults = buildInitialFilters(definition)
    setFilterValues(defaults)
    setSavedFiltersNotice(false) // values are defaults again — notice is stale
    /*
     * E4: the panel now says one thing and the grid another. Say so, and put the
     * run control back into its pre-run state; do NOT re-run (see resetSinceRun).
     *
     * Gated on the values ACTUALLY moving, not on the click. Resetting a report
     * that is already at its defaults changes nothing, and claiming a grid is
     * stale when it is not is a false statement about a financial figure — it
     * also flips a perfectly good "Refresh" back to "Run report" for no reason.
     * Same order-insensitive comparison the saved-filters banner uses at :673,
     * for the same reason (a stored set can carry identical values under a
     * different key order).
     *
     * `filterValues` is therefore a dependency. That re-creates this callback on
     * every filter edit, which is cheap — it is passed to ReportFilterPanel,
     * which is not memoized — and it is the only way to read the pre-reset values
     * without an impure state updater (React double-invokes those under
     * StrictMode).
     */
    setResetSinceRun(stableStringify(defaults) !== stableStringify(filterValues))
  }, [definition, reportId, userKey, filterValues])

  // CC-49893: the banner's reset — restore defaults AND re-run immediately so
  // the grid never keeps showing saved-filter data under a defaults panel.
  const resetSavedFilters = useCallback(() => {
    if (!definition) return
    onResetFilters()
    runReportWith(buildInitialFilters(definition), { trigger: 'reset_saved_filters' })
  }, [definition, onResetFilters, runReportWith])

  // Active filter array passed to export
  const activeFilters = useMemo(
    () => filtersAsArray(filterValues, definition?.columns, definition?.reportType),
    [filterValues, definition]
  )

  // --- scheduling from this report (spec 058) --------------------------------
  // Both paths send the SAME filter array the Run button would, so a scheduled
  // run is the run you were about to make — just later, or repeatedly.
  const submitSchedule = useCallback(async (mode, payload, delivery = {}) => {
    // Same required-date guard the interactive Run enforces (emptyDateFilters):
    // scheduling/queuing a report with an empty required date silently produced a
    // run with no filters, which CB's report engine 500s on. Block it here too and
    // tell the user which filter to fill, instead of scheduling a doomed run.
    const missingDates = emptyDateFilters(definition, filterValues)
    if (missingDates.length > 0) {
      toast.error(`Set a date filter first: ${missingDates.map((c) => c.label).join(', ')}.`)
      return
    }
    setScheduleSubmitting(true)
    try {
      if (mode === 'schedule') {
        await reportScheduleService.create({
          kind: 'generic',
          reportId,
          name: payload.name || definition?.name,
          filters: activeFilters,
          recurrence: payload.recurrence,
          rollForward: payload.rollForward,
          retainCount: payload.retainCount,
          deliverEmail: payload.deliverEmail,
          deliverFormat: payload.deliverFormat,
          // Spec 138: the schedule's other recipients — forwarded, never dropped, on
          // BOTH hosts of this modal (the Queue page's picker forwards them too).
          deliverRecipients: payload.deliverRecipients,
          // Spec 139: the address an owner with no email of their own supplied — present
          // only when the modal asked for it (no key otherwise), forwarded by BOTH hosts.
          ...(payload.deliverOwnerAddress !== undefined ? { deliverOwnerAddress: payload.deliverOwnerAddress } : {}),
          /*
           * spec 120. These three were computed by the modal and thrown away
           * here — only `useQueueReportPicker` forwarded them — so a user who
           * chose "Everyone at the firm" or a group on a REPORT PAGE got a 201
           * and a schedule that saved as `scope='mine'` (cb-api defaults an
           * absent scope, `schedules.js` → `normalizeScope`). They were told
           * something about who can see a financial report and it was not true.
           * Nothing else changes: the server still VALIDATES a group scope
           * against CB group membership and still narrows an unrecognised value
           * to 'mine', so forwarding can only make the saved row match what the
           * user picked.
           */
          scope: payload.scope,
          groupId: payload.groupId,
          groupName: payload.groupName,
        })
        // The schedules list and the Queue page's count chip both read one cached
        // key; nothing here marked it stale, so a schedule created from a report
        // page stayed invisible on Manage Schedules until a hard refresh.
        invalidateSchedules()
        toast.success(`${definition?.name || 'Report'} is scheduled`)
      } else {
        await reportJobService.create({
          kind: 'generic', reportId, filters: activeFilters, scheduledFor: payload,
          ...(delivery.deliverEmail ? { deliverEmail: true, deliverFormat: delivery.deliverFormat } : {}),
        })
        toast.success(`${definition?.name || 'Report'} is scheduled to run`)
      }
      setScheduleWhen(null)
    } catch (err) {
      toast.error(err?.message || 'Could not schedule that report.')
    } finally {
      setScheduleSubmitting(false)
    }
  }, [reportId, definition, activeFilters, filterValues, invalidateSchedules])

  // CC-49320: the "no constraint" value a filter returns to when its applied
  // criterion is removed from the pill bar. Dropdown-likes go back to CB's
  // explicit "All"; DATE ranges go to All Dates; everything else empties (an
  // empty value is omitted from the run request — see filtersAsArray). RADIO
  // is not here: radios always carry a value (CC-49861), so their pills aren't
  // removable — clearing one to "$$cb_all" fed the CC-49871 SQL error.
  const clearedValueFor = (col) => {
    const t = col.filterTypeName
    if (t === 'DATE') return ALL_DATES
    if (t === 'DROPDOWN' || t === 'CHECKBOX' || t === 'ACCOUNTSDROPDOWN') return ALL_DATES
    return ''
  }

  // Remove one applied criterion (or all of them) and re-run immediately so the
  // pill bar always reflects the data on screen (CC-49320: criteria removable).
  const removeFilter = useCallback((columnId) => {
    const col = (definition?.columns || []).find((c) => c.columnId === columnId)
    if (!col) return
    const next = { ...filterValues, [columnId]: clearedValueFor(col) }
    setFilterValues(next)
    runReportWith(next, { trigger: 'filter_pill_removed' })
  }, [definition, filterValues, runReportWith])

  const clearAllFilters = useCallback(() => {
    if (!definition) return
    const next = { ...filterValues }
    for (const col of definition.columns || []) {
      if (!col.filterable) continue
      // SIMPLEDATE stays put — it's a required as-of parameter, not a criterion
      // (CB's procs crash on an empty single date; see emptyDateFilters).
      // RADIO and BOOL stay put too — each always carries exactly one value
      // (CC-49861 / CC-49925); "clearing" would leave a value the wire omits.
      if (col.filterTypeName === 'SIMPLEDATE' || col.filterTypeName === 'RADIO' || col.filterTypeName === 'BOOL') continue
      next[col.columnId] = clearedValueFor(col)
    }
    setFilterValues(next)
    runReportWith(next, { trigger: 'filters_cleared' })
  }, [definition, filterValues, runReportWith])

  // The "Applied filters" pill bar — the PANEL's current criteria. These chips
  // are editable (removing one re-runs), so they must track `filterValues` and
  // not the run. Rules and rationale live on buildFilterPills above.
  const appliedFilterPills = useMemo(() => buildFilterPills(definition, filterValues), [definition, filterValues])

  // CC-49336: apply a named view — restore its filter selections and re-run;
  // the layout half was already written to preferencesStore by the menu.
  const applyNamedView = useCallback((view) => {
    const filters = view?.filters || {}
    setFilterValues(filters)
    // CC-49732: v2 views restore the whole page state — Group By + the grid's
    // quick-filters/toggles (seeded into the remounted grid). v1 views keep
    // their original behavior (filters + layout only).
    if (view?.v >= 2 && view.pageState) {
      appliedGridStateRef.current = view.pageState.grid || null
      setGroupBy(view.pageState.groupBy || '')
    } else {
      appliedGridStateRef.current = null
    }
    setViewNonce((n) => n + 1)
    runReportWith(filters, { trigger: 'view_applied' })
  }, [runReportWith])

  /*
   * CC-49325/49326 — one-line summary of the applied criteria (incl. the date
   * range and its basis column) for a DOCUMENT's header.
   *
   * R3 (2026-08-28) — built from the filters the displayed result was RUN with,
   * NOT from the panel. This is the fix for the wrong-document defect: the three
   * consumers below (client export header, print masthead, AI scope line) all
   * describe figures that are already on screen, and the panel can move away
   * from those figures without a run — via "Reset filters", which now says so,
   * and equally via editing any filter and not clicking Run, which does not.
   * Both used to relabel the document silently; neither can now.
   *
   * The on-screen chips deliberately keep reading `filterValues`: they ARE the
   * panel — removable, and removing one re-runs. Only the surfaces that leave
   * the screen switch to the run's values.
   *
   * Before the first run there is nothing to mislabel, so it falls back to the
   * panel: printing an un-run report gives a masthead over an empty page.
   *
   * NOT changed: `activeFilters`, the array ReportExport receives as `filters`
   * and hands to `reportService.exportUrl`. That is the SERVER export, which
   * re-runs server-side with whatever the panel currently says and is
   * authoritative; its scope is its own request, not this result. Checked
   * before splitting them: `filtersSummary` is read ONLY on ReportExport's
   * client-built branches (its `filtersLine`, ReportExport.jsx:172/190/224), so
   * the server download never carries this string at all.
   */
  const documentFiltersSummary = useMemo(() => {
    const ran = runResult?.filterValues
    return pillsToLine(ran ? buildFilterPills(definition, ran) : appliedFilterPills)
  }, [definition, runResult?.filterValues, appliedFilterPills])

  /*
   * Spec 176 (CC-54206) — the rows on screen were run with a different scope than the
   * run the user last asked for. Compared on the WIRE (`wireScope`), so a re-run of the
   * same filters — a Refresh that fails, say — is not called "different filters": its
   * rows still answer the panel, only from an earlier moment. An ordinary edit without
   * a run never sets `askedFilters`, so it does not raise this (E4's scope rule).
   */
  const supersededScope = useMemo(() => {
    if (!definition || !askedFilters || !runResult?.filterValues) return false
    return wireScope(runResult.filterValues, definition) !== wireScope(askedFilters, definition)
  }, [definition, askedFilters, runResult?.filterValues])

  // ---- render ----
  /*
   * A1 (2026-08-26) — `.report-shell`, not `w-full`.
   *
   * This page has FIVE top-level returns. Three (loading / blocked / main) used
   * the shell; this one and "Report not found" below were still w-full, so an
   * error panel ran edge to edge with only main's 24px while the report it is
   * standing in for sits 90px in. `w-full` is dropped rather than kept: the
   * shell sets width:100% itself, and .report-shell is UNLAYERED, so a layered
   * w-full utility on the same element would be dead code either way.
   *
   * Worth recording because it was believed done: commit e872bf3's message and
   * specs/092-report-chrome-refresh-piece-1.md:826 BOTH already claim these two
   * branches were migrated. They were not — only three of the five were.
   */
  if (!Number.isFinite(reportId)) {
    return (
      <div className="report-shell text-center py-16">
        <p className="text-gray-500">Invalid report id</p>
        <button onClick={() => navigate(backTo)} className="text-cb-accent-text text-sm mt-2 hover:underline">
          Back to Reports
        </button>
      </div>
    )
  }

  /*
   * `defLoading` is react-query's `isLoading`, i.e. `isPending && isFetching` — so
   * it is FALSE for a definition read that is pending but not in flight. When
   * react-query pauses the fetch (default `networkMode: 'online'`) `queryFn` is
   * never called, `defError` stays null and `definition` stays undefined, which
   * fell straight through to `!definition` below and told the user
   * **"Report not found"** about a report that exists — with an empty red line
   * underneath, because there was no error message to print.
   *
   * That is the companion symptom to the catalog's "All Reports 0" (spec 087 §4),
   * reported together, and it is the more alarming of the two: it names a specific
   * report as missing. Same primitive, same three-state distinction.
   *
   * `idle` deliberately does NOT join the loading branch here, unlike on the
   * catalog — and it is currently UNREACHABLE on this page, which is worth
   * writing down because it is not obvious. This query carries
   * `enabled: Number.isFinite(reportId)`, but a non-finite id never gets this far:
   * the `!Number.isFinite(reportId)` guard above returns "Invalid report id"
   * first. For a finite id the query is always enabled.
   *
   * So if someone later adds an `enabled` condition here, `idle` becomes
   * reachable and falls through to "Report not found" below — reintroducing
   * exactly the lie this block fixes. Give it its own branch at that point rather
   * than assuming the fall-through is harmless.
   */
  if (defState === 'loading') {
    return (
      <div className="report-shell">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 flex justify-center">
          <LoadingSpinner message="Loading report…" />
        </div>
      </div>
    )
  }

  if (defState === 'blocked') {
    return (
      <div className="report-shell" role="alert">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can’t reach the server, so we haven’t loaded this report.</p>
          <p className="mt-1 text-amber-800">
            This does not mean the report is missing — your connection looks offline. Check it and try again.
          </p>
          <button
            type="button"
            onClick={() => refetchDefinition()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (defError || !definition) {
    return (
      /* .report-shell for the same reason as the invalid-id branch above — this
         is the second of the two that were claimed migrated and were not. */
      <div className="report-shell text-center py-16">
        <p className="text-gray-500">Report not found</p>
        <p className="text-xs text-red-500 mt-1">{defError?.message}</p>
        <button onClick={() => navigate(backTo)} className="text-cb-accent-text text-sm mt-2 hover:underline">
          Back to Reports
        </button>
      </div>
    )
  }

  // If this report has a specialized renderer registered for its actionKey,
  // hand off entirely. The specialized component owns its own layout, filters,
  // and data flow — none of the generic FilterPanel/ReportTable code runs.
  const Specialized = getRendererForActionKey(definition.actionKey)
  if (Specialized) {
    // Key on jobId so navigating between the live view and a saved result (or
    // between two saved results) REMOUNTS the renderer — otherwise only the query
    // string changes, the component stays mounted, and its consume-once jobId ref
    // never re-reads the new jobId ("clicked the tray link, nothing reloaded").
    return (
      /* CC-50291: the ONE place all 21 specialized pages are mounted, and the
         only place that knows their actionKey. Providing the alias context here
         reaches every page without editing any of them — the same trick
         reportExportViewContext uses. Keyed by actionKey, which is the
         specialized half of the backend's report_key (spec 061 §2). */
      <ColumnAliasProvider reportKey={definition.actionKey}>
        {/* Spec 168: the same trick for the reopen guard — `useLatestRun` refuses a
            `?jobId=` run whose report is not this one, on all 21 pages. */}
        <ReopenTargetContext.Provider value={jobIdParam ? { reportId, jobId: jobIdParam } : null}>
          <Specialized
            key={`${reportId}:${jobIdParam || 'live'}`}
            reportId={reportId}
            name={definition.name}
            summary={definition.summary}
            jobId={jobIdParam}
          />
        </ReopenTargetContext.Provider>
      </ColumnAliasProvider>
    )
  }

  /*
   * Column aliases must be applied where the column list ENTERS the page — and on
   * this path there are TWO such doors, not one. Only the first was wired.
   *
   * `rawDefinition` (aliased at :236) is the report SKELETON. It feeds the filter
   * bar, the Group by list and the Rename-columns editor. The grid does not render
   * it: the grid renders the RUN RESULT, `runResult.report`, which arrives fresh
   * from CB carrying CB's own labels.
   *
   * So renaming a column updated the modal and left the grid header underneath it
   * unchanged — measured on deployed QA, report 190: the aliases were correctly
   * stored (`{"1194":"aTeam Member","1198":"monies"}`) against column ids that
   * match the definition exactly (1194 = Timekeeper, 1198 = Billable $ Total), and
   * the header still said "Timekeeper". Nothing was wrong with the write; the read
   * that the grid uses simply never had the transform applied.
   *
   * This is all 119 generic reports, flat and grouped — both read `displayReport`,
   * which derives from here. The 21 specialized pages were never affected: they
   * resolve each label at RENDER time through `useAliasedLabel`
   * (`columnAliasContext`), a second implementation that `ReportDataGrid` and
   * `ReportGroupedGrid` do not consume. Two mechanisms for one feature is why this
   * looked like it worked.
   *
   * `aliasDefinition` is shape-generic — it rewrites `.columns` and returns the
   * SAME object reference when nothing applies, so this stays memo-safe and the
   * ticking-value-in-a-columns-memo trap stays shut. `columnId` is never touched,
   * so no wire key can inherit a firm's word.
   */
  const reportData = aliasedRunReport
  const processId = runResult?.processId
  // CC-49458: what the grid actually renders — regrouped when Group By is set.
  const displayReport = groupBy && reportData ? groupFlatReport(reportData, groupBy) : reportData
  const groupByOptions = reportData ? groupableColumns(reportData.columns || []) : []

  /*
   * E4 — the two-state run control's signal, computed ONCE.
   *
   * spec 092 gave the run control two states and drove them from `hasRun`:
   * "Run report" (blue primary CTA) before there is a result, neutral "Refresh"
   * after. Until now that was literally `!!reportData` written out twice — once
   * as the ReportFilterPanel prop and once in the ReportRunStateContext value.
   *
   * A filter reset is the case where "there is a result" and "the result answers
   * what the panel currently says" come apart, so the question the control is
   * really asking is the second one. `resetSinceRun` is subtracted here rather
   * than in a second piece of run-control state, and the SAME const feeds both
   * consumers, so the prop and the published value cannot drift apart.
   *
   * Nothing under ReportView's provider reads `useReportHasRun` today (the
   * readers are SpecializedFilterBar / AutoReportGrid / BalanceSheetReport, none
   * of which mount here). Passing the same value anyway is what keeps that true
   * if one ever does.
   */
  const hasRun = !!reportData && !resetSinceRun
  // The grid is showing figures the filters above it no longer describe.
  const staleSinceReset = !!reportData && resetSinceRun
  const storageKey = `report:${definition?.actionKey || reportId}`

  // Hand the run off to the queue once it passes the threshold — or immediately
  // when the user explicitly chose "Run in the background now". Only meaningful
  // when the job system is on; without it there is no queue to hand off TO, so
  // the original spinner remains the whole story.
  const handedOff = REPORT_JOBS_ON
    && (forceHandoff
      || (runStartRef.current != null && Date.now() - runStartRef.current >= QUEUE_HANDOFF_MS))

  /*
   * "…and you get your table back" — Himaad's Section 1c (`Run / Moved to tray`).
   *
   * Once a run hands off to the queue it is no longer the thing on this screen,
   * so the PREVIOUS result comes back rather than the page sitting empty behind a
   * waiting panel. Every surface that renders the result reads this instead of
   * `!running`, so they cannot drift apart: the grid, the report header/footer
   * HTML, the chart badge and the AI panel are one state, not four.
   *
   * What happens when the handed-off run LANDS — corrected by spec 131. This
   * comment used to say the new result is NOT swapped in ("that stays a click on
   * Open in the tray", citing Himaad's 1d frame). The code never did that: the
   * run's result has always replaced the table when it landed, and it still does
   * — Jordan's decision 1(a), 2026-09-30, which knowingly departs from the 1d
   * frame. What changed is that it lands ONLY while it is still the latest run
   * on this page: a newer run always wins, and "Dismiss run" or Cancel retire it
   * so nothing swaps in after either. The AI panel is keyed on the committed
   * result's identity (`runResult.seq`), so a swapped-in result also starts a
   * fresh summary rather than keeping one written about the rows it replaced.
   * To honour 1d instead, retire the run at hand-off — one line.
   *
   * It does not resurrect anything on a FIRST run. There is no previous result to
   * come back to, so `displayReport` is null and the surfaces stay unmounted —
   * the handoff panel is the whole story, as before.
   *
   * The staleness of what returns is already carried: `staleSinceReset` bands
   * above, and the result is whatever it was before this run started.
   */
  const showResult = !running || handedOff

  /* spec 092 — Bar 1 on the generic engine (119 reports). The other host is
     SpecializedReportShell (21). Those two are the whole of report chrome. */

  const gridBackedReport = displayReport && (isFlatReport(displayReport) || isGroupedReport(displayReport))

  /* Export acts on the report as an object, so it belongs in Bar 1 — the
     designer's rule for that row. Gated exactly as it was in the grid toolbar:
     there is nothing to export before a run. */
  const exportControl = !running && reportData ? (
    <ReportExport
      reportId={reportId}
      filters={activeFilters}
      processId={processId}
      rowCount={reportData?.recordCount || 0}
      reportName={definition.name}
      reportSummary={definition.summary}
      /* The RUN's criteria, not the panel's — see documentFiltersSummary. Only
         ReportExport's client-built branches read it; the server download
         re-runs and stamps its own header. */
      filtersSummary={documentFiltersSummary}
      firmName={firmName}
      gridFiltered={gridFiltered}
      getView={gridBackedReport ? () => exportViewRef.current?.() : undefined}
      hasGroups={!!displayReport && isGroupedReport(displayReport)}
    />
  ) : null

  /* The Ask trigger. Previously an inline tertiary link in the grid toolbar that
     appeared ONLY while the panel was collapsed, so there was no way back once you
     had opened it except the panel's own "Hide". It is a real toggle now, which is
     also what lets it carry aria-expanded honestly.
     Purple is fenced to this control and nothing else (designer's rule). */
  /*
   * spec 092 — the applied-criteria chips, in the mock's chip treatment
   * (Figma 110:8927). Extracted so the SAME row can sit inside the query card
   * while the panel is open and inside the collapsed summary bar when it is not.
   *
   * Removability, label resolution and the re-run on removal are all unchanged:
   * `removeFilter` still routes through `clearedValueFor`, and BOOL / RADIO /
   * SIMPLEDATE stay non-removable because they have no unset state — an absent
   * filter is a scope-WIDENING gesture on this backend, not a neutral one.
   */
  /*
   * E2 (2026-08-27) — a FUNCTION now, not a single element, because the two
   * render sites need different answers to "does this chip duplicate something
   * already on screen?".
   *
   * - Open query card: DATE / SIMPLEDATE controls are rendered INLINE there now,
   *   so their chips restate a control the user is looking at → omitInline.
   * - Collapsed strip: "Hide filters ▲" unmounts the entire panel
   *   (ReportFilterPanel.jsx:1185-1191), inline control included, so the chip is
   *   the ONLY statement of the period on screen → keep every chip.
   *
   * The filter lives HERE and nowhere else. `appliedFilterPills` itself stays
   * whole: see the note on `filterTypeName` in that memo — it is the source of
   * the export header, the print masthead and the AI scope line.
   *
   * `whenEmpty` exists so a caller's empty-state text and its chips are decided
   * from the SAME array, computed once below. The trap it closes: the collapsed
   * strip used to test `appliedFilterPills.length === 0` beside a row built from
   * a different list, so a report whose only applied criterion is a date would
   * have collapsed to a blank chip row instead of "Report defaults". Pinned by a
   * test in ReportView.test.jsx.
   */
  const renderChipRow = ({ omitInline, whenEmpty = null }) => {
    const shown = appliedFilterPills.filter(
      (p) => !(omitInline && INLINE_FILTER_TYPES.has(p.filterTypeName))
    )
    // Safe only because the sole `whenEmpty` caller passes omitInline: false —
    // there `shown` is `appliedFilterPills`, so empty means no criteria at all
    // and there is no removable filter for "Clear all" to act on either.
    if (shown.length === 0 && whenEmpty) return whenEmpty
    return (
      <div className="flex flex-wrap items-center gap-2">
        {shown.map((p) => (
          <span
            key={p.columnId}
            className="inline-flex h-[30px] items-center gap-2 rounded-rc-card border border-rc-chip-line bg-rc-chip-bg px-2.5 text-[13px] font-medium text-rc-chip-text"
          >
            <span className="font-semibold">{p.label}:</span>
            <span className="font-normal">{p.value}</span>
            {p.removable && (
              <button
                type="button"
                onClick={() => removeFilter(p.columnId)}
                aria-label={`Remove ${p.label} filter`}
                title={`Remove the ${p.label} filter and re-run`}
                className="-mr-1 rounded p-0.5 leading-none opacity-70 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-current"
              >
                {'×'}
              </button>
            )}
          </span>
        ))}
        {/* Deliberately reads the UNFILTERED array: `clearAllFilters` (:767)
            still clears the date, so hiding this control when the only removable
            criterion is a suppressed date chip would hide a working action. The
            visible cost is that a date-only report shows "Clear all" in the open
            card with no chip beside it — accepted for this batch. */}
        {appliedFilterPills.some((p) => p.removable) && (
          <button type="button" onClick={clearAllFilters} className="btn-cb-tertiary">
            Clear all
          </button>
        )}
      </div>
    )
  }

  /*
   * Group by and Saved views act on the QUERY, so the mock puts them in the query
   * card beside the run control rather than in a loose row above the grid. The
   * panel already has an `actions` slot rendered inline with Reset/Run — the same
   * slot the 20 specialized pages use — so they go there instead of needing new
   * layout.
   */
  const queryActions = reportData ? (
    <>
      {groupByOptions.length > 0 && (
        /* `gap-1.5` removed with the same B3 sweep as the h-[30px] pairs below:
           .btn-rc-pill is unlayered and sets gap:0.5rem, so the rendered gap was
           always 8px and the utility only claimed otherwise. No visual change. */
        <label className="btn-rc-pill cursor-pointer">
          <span className="text-rc-muted">Group by:</span>
          <select
            aria-label="Group by"
            value={groupBy}
            onChange={(e) => {
              // Manual regroup = leaving the applied view's page state; the
              // remounted grid goes back to default toggles (CC-49732).
              appliedGridStateRef.current = null
              setGroupBy(e.target.value)
            }}
            className="select-rc-bare"
          >
            <option value="">{isGroupedReport(reportData) ? 'Report default' : 'None'}</option>
            {groupByOptions.map((c) => (
              <option key={c.columnId} value={String(c.columnId)}>{c.label}</option>
            ))}
          </select>
        </label>
      )}
      <ReportViewsMenu
        storageKey={storageKey}
        currentFilters={filterValues}
        onApply={applyNamedView}
        getPageState={() => ({ groupBy, grid: pageStateRef.current?.() || null })}
      />
    </>
  ) : null

  /*
   * "Rename columns" for the grid's result strip, left of Display View.
   *
   * Admin-only, and only when the alias read actually SAID so — `canManage` is
   * resolved server-side against CB's own permission and is never inferred here,
   * so this is absent for everyone else rather than present-but-failing.
   *
   * Published through ReportRunStateContext instead of passed as a prop, because
   * the specialized host renders its grid as `{children}` and cannot pass one;
   * both hosts therefore use the same mechanism rather than two.
   */
  const stripActions = canManageAliases ? (
    <button
      type="button"
      onClick={() => setRenamingColumns(true)}
      /* B3 (2026-08-26): `h-[30px] text-[13px]` removed, not overridden. It was
         already dead — src/index.css has no @layer, Tailwind emits utilities
         inside `@layer utilities`, and an unlayered rule beats a layered one at
         any specificity, so `.btn-rc`'s 38px/14px always won. Left in place it
         only tells the next reader this button is 30px, which it never was. */
      className="btn-rc"
    >
      Rename columns
    </button>
  ) : null

  const askTrigger = AI_REPORT_SUMMARY && aiMode !== 'off' && !running && gridBackedReport ? (
    <button
      type="button"
      onClick={() => setAiCollapsed((c) => !c)}
      aria-expanded={!aiCollapsed}
      aria-controls={AI_PANEL_ID}
      className="btn-rc btn-rc-ai"
    >
      <span aria-hidden="true">&#10024;</span>
      {/* "AI Summary", not "Summary": the report page already carries a
          human-written summary line under its title, so a bare "Summary" button
          reads as a jump-link to that rather than a generated one. The chat mode
          keeps "Ask" — that verb is unambiguous on its own. */}
      {aiMode === 'chat' ? 'Ask' : 'AI Summary'}
    </button>
  ) : null

  return (
    <div className="report-shell">
      <ReportActionBar reportName={definition.name} reportSummary={definition.summary} onBack={goBack} reportsHref={backTo} ask={askTrigger}>
        {exportControl}
      </ReportActionBar>


      {/* Reopened from the tray (?jobId) → mark it a saved snapshot with its run time. */}
      <SavedResultBanner asOf={savedAsOf} />

      {/* CC-49339/CC-49340: print/PDF header — firm, run timestamp, and the
          applied criteria (incl. date range). The on-screen h1 already prints;
          this block only exists on paper. */}
      {/* CC-49844: "Data only" suppresses the print masthead here too, so the
          generic engine behaves the same way as the specialized pages. */}
      {/* R3: the criteria line names the filters the GRID BELOW was run with.
          The filter panel carries data-print-hide and print-report.css hides
          every input/select, so paper shows this masthead and the grid and
          nothing else — the printed page is therefore self-consistent even
          while the on-screen panel says something different. That is why the
          stale band is not also forced into print: on paper there is no panel
          for it to contradict, and a second, hedging sentence beside a header
          that is already correct reads as doubt about the figures. */}
      {!exportDataOnly && (
        // testid so a test can assert THIS block's criteria line without also
        // matching the on-screen chip row, which after a reset deliberately
        // states something different. The two disagreeing is the whole point.
        <div data-testid="report-print-masthead" className="hidden print:block mb-8 text-center text-gray-900">
          <p className="text-xl font-bold uppercase tracking-wide">{firmName}</p>
          {documentFiltersSummary && <p className="text-base mt-1">{documentFiltersSummary}</p>}
          <p className="text-sm text-gray-600 mt-1">
            Generated {fmtDisplayDateTime(new Date())}
            {user?.name ? ` · Printed by ${user.name}` : ''}
          </p>
        </div>
      )}

      {/* CC-49893: saved-filters banner. Persistence (CC-49335) restores the
          last-used selections on load; when they differ from the defaults,
          say so LOUDLY — a silently narrowed report reads as missing data.
          Product decision 2026-07-30: visibility banner over per-page
          persistence exemptions. Stays visible when the panel collapses. */}
      {/* spec 092: was a bespoke coloured div, one of five. Now the shared band —
          same words, same two controls, one set of colours and one announcement. */}
      {savedFiltersNotice && (
        <ReportBand
          variant="info"
          className="mb-4"
          action={{
            label: 'Reset to defaults',
            onClick: resetSavedFilters,
            dismissLabel: 'Dismiss saved-filters notice',
          }}
          onDismiss={() => setSavedFiltersNotice(false)}
        >
          <span className="font-semibold">Filters from your last visit are applied</span>
          {' — '}the results may be narrower than the report&apos;s defaults.
        </ReportBand>
      )}

      {/* Filters — full panel while editing; collapses to the summary bar below
          once a report has run, reclaiming its height for the grid. */}
      {(!filtersCollapsed || runError) && (
        <ReportFilterPanel
          columns={definition.columns || []}
          values={filterValues}
          onChange={onFilterChange}
          onRun={runReport}
          // Only offer the split Run/Queue control where there IS a queue to run
          // into — with the job system off, ReportFilterPanel renders its plain
          // Run button exactly as before.
          // "Run in the background now" opens the queue modal on the "now" tab (not a
          // direct fire-and-forget) — the email/delivery toggle lives ONLY in that
          // modal, so a direct run gave no way to opt into "email me when it's ready".
          onRunBackground={REPORT_JOBS_ON ? () => setScheduleWhen('now') : undefined}
          // "Queue for later" / "Set up a recurring schedule" open the queue
          // modal on THIS report, carrying the filters currently on screen —
          // which is the whole reason scheduling starts from a report page.
          onSchedule={REPORT_JOBS_ON ? setScheduleWhen : undefined}
          onReset={onResetFilters}
          /* spec 092: chips inside the card while the panel is open; the collapsed
             summary bar below carries them when it is not.
             E2 (2026-08-27): omitInline — the DATE / SIMPLEDATE controls render
             inline in this card now, so their chips would restate a control two
             rows away. The gate stays on the UNFILTERED length so a date-only
             report still gets the row (and its "Clear all"); the only caller of
             `chips` in the repo is this line — SpecializedFilterBar.jsx:183-199
             passes none, so the 21 specialized pages have no applied-criteria row
             to de-duplicate in the first place. */
          chips={!running && !runError && appliedFilterPills.length > 0 ? renderChipRow({ omitInline: true }) : null}
          hasRun={hasRun}
          actions={queryActions}
          onCollapse={reportData ? () => setFiltersCollapsed(true) : undefined}
          /*
           * Section 1c: on hand-off the split button returns to "Run report",
           * enabled — the run is the queue's business now, so the control that
           * starts one must not stay locked out. Left as plain `running` it read
           * "Running…" indefinitely behind the tray, which both blocked a second
           * run and implied this page was still doing the work.
           */
          isLoading={running && !handedOff}
          enableDateBasis={dateBasisEnabled(definition)}
          accountIds={definition.filteredAccountIds}
        />
      )}

      {/* Run status / errors.
          role="alert" — spec 152 §17 (live QA 2026-10-12): this band carries spec 152's
          reopen sentences ("This saved result is no longer stored…", "This run is no
          longer available…") and every other run failure, and it was a bare div, so a
          screen reader heard nothing (SC 4.1.3). The Queue page's band for the same
          situation (`queue-jobid-unresolved`) is role="alert" for the same reason: the
          user asked for a specific result and did not get it, which should not wait for a
          pause in the reader's queue. The band mounts WITH its text, which is when an
          alert is announced. Tests find it by its testid — the filter panel above it can
          render alerts of its own. */}
      {runError && (
        <div role="alert" data-testid="report-run-error" className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6 text-sm text-red-700">
          {runError}
        </div>
      )}

      {/* Spec 176 (CC-54206) — the page stopped following a run that did NOT fail. An
          `info` band, not the red one: the last thing the server said is that the run
          is going (or done), and the Queue holds its result. role="status" (the band's
          default) — nothing here was refused, so it can wait for a pause. The link
          inherits the band's own text colour, which index.css records as AA on it. */}
      {runNotice && (
        <ReportBand variant="info" className="mb-4" data-testid="report-run-lost">
          {runNotice === 'finished' ? FINISHED_UNREAD_LEAD : STILL_RUNNING_LEAD}{' '}
          <Link to={QUEUE_PATH} className="font-medium underline decoration-1 underline-offset-2 hover:no-underline">
            Open the Queue
          </Link>
          {runNotice === 'finished' ? FINISHED_UNREAD_TAIL : STILL_RUNNING_TAIL}
        </ReportBand>
      )}

      {/* Once a run passes QUEUE_HANDOFF_MS (or the user chose "run in the
          background"), the handoff panel REPLACES this area — it never stacks, so
          the page can't grow a second waiting state. Below the threshold the
          original spinner block is unchanged. */}
      {running && handedOff && (
        <QueueHandoffPanel
          percent={progress?.percent ?? 0}
          message={progressLabel(progress?.message)}
          elapsedMs={runStartRef.current ? Date.now() - runStartRef.current : 0}
          jobId={activeJobId}
          notifyArmed={notifyArmed}
          onNotifyMe={() => {
            setNotifyArmed(true)
            // The tray already fires a browser notification on running→ready when
            // the tab is unfocused; asking here just secures permission early.
            if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
              Notification.requestPermission().catch(() => {})
            }
            toast.info('We’ll let you know when it’s ready.')
          }}
          onStayAndWatch={() => setForceHandoff(false)}
          onDismissRun={() => {
            // "Dismiss", not cancel — the server keeps working; this only stops
            // this page waiting on it. The job stays in the queue.
            //
            // Spec 131 decision 2: and it RETIRES the run, so its result no longer
            // swaps onto this page when it lands. The toast sends the user to the
            // queue for it; before this, the figures then replaced the table
            // anyway, minutes later, under whatever the user was reading.
            retireRun()
            setRunning(false)
            setProgress(null)
            toast.info('Left running in the background — find it in your queue.')
          }}
          // Deliberately generic and TRUE. The mock shows a per-report statistic
          // ("usually returns about 28,900 rows in 2–3 minutes"); we have no
          // per-report timing history to compute that from, and inventing a
          // number on a financial report is worse than omitting it. Wire the real
          // figure in once job history is queryable per report+filters.
          typicalHint="Large reports finish faster with a filter — a matter, client, or timekeeper."
        />
      )}

      {/*
        * Section 1b (`Run / Running inline`): the in-flight state is a BAND in the
        * page's own flow, not a 12rem-tall centred spinner card that displaced the
        * grid. Three things follow from that, and they are the point of the change:
        *
        *   1. The page stops jumping. The old card replaced the grid card, so
        *      starting a run moved everything below it and finishing moved it back.
        *   2. It composes. A band can sit above a table, which is what makes the
        *      handed-off state below ("you get your table back") possible at all.
        *   3. The elapsed clock is honest and always present, rather than appearing
        *      only after 8 seconds and only when jobs were enabled.
        *
        * The removed 8-second copy claimed "this runs in the background, so it
        * won't time out" from the FIRST moment of every run — including runs that
        * had not handed off and would not, because `forceHandoff` was false and the
        * threshold had not passed. The band's own sentence is conditional instead.
        *
        * `progressLabel(progress?.message)` is deliberately NOT rendered here.
        * CC-49982: CB emits failure-sounding status strings mid-run, and echoing
        * one read as "the report crashed" seconds before the rows arrived. The band
        * says what is true — this report is running, for this long — and nothing
        * about CB's internal step names. The percentage goes with it: it came from
        * the same source and was frequently 0 for the whole run.
        */}
      {running && !handedOff && (
        <RunningReportBand
          /* A new run is a new band: the key re-seeds its elapsed clock so a
             second run cannot briefly show the first one's figure. */
          key={runStartRef.current}
          className="mb-4"
          reportName={definition?.name}
          startedAt={runStartRef.current}
          /* Only promise the hand-off where it can actually happen. With
             VITE_REPORT_JOBS off there is no job, no tray and no threshold. */
          handsOffAfterMs={REPORT_JOBS_ON ? QUEUE_HANDOFF_MS : null}
          onCancel={() => {
            /*
             * Real cancel where there is a job to cancel, and only what it can
             * deliver where there is not.
             *
             * With a job id we call the server route, which marks the row
             * `cancelled` and deletes its stored result. The CB-side query is NOT
             * abandoned (CB has no cancel hook — cb-api jobs/routes.js:451), so
             * the toast says "stopped waiting", never "stopped".
             *
             * Fire-and-forget is deliberate: the user's intent is to stop looking
             * at this, and blocking their UI on a cleanup call they did not ask
             * to watch would be worse than a stale row they can dismiss.
             */
            if (activeJobId) reportJobService.cancel(activeJobId).catch(() => {})
            // Spec 131 decision 2: the toast promises the user stopped waiting, so
            // nothing from this run may land afterwards. Before this, a run that
            // finished anyway replaced the screen later with no spinner — on the
            // prod path (no job to cancel; runAndWait simply ran on) and on a
            // joined ready job whose rows were already being read. (A polled job
            // that the cancel reached instead looped forever on `cancelled`,
            // which is the terminal state the poll loop now honours.)
            // `activeJobId` is the LATEST run's job — its write is gated — so the
            // cancel above reaches the run the user was actually watching.
            retireRun()
            setRunning(false)
            setProgress(null)
            toast.info('Stopped waiting. The database query may still finish on its own.')
          }}
        />
      )}

      {/* spec 092 — the COLLAPSED summary only. While the panel is open the chips
          live inside the query card (passed as `chips`), which is where the mock
          puts them; a second card floating below it was the old layout. This bar
          remains for the collapsed state, where it is the only way back in. */}
      {!running && !runError && reportData && filtersCollapsed && (
        <div data-print-hide className="mb-4 flex flex-wrap items-center gap-2 rounded-rc-card border border-rc-line bg-white px-4 py-2.5 shadow-rc-card">
          {/* E2 (2026-08-27): omitInline FALSE here, and it is load-bearing.
              "Hide filters ▲" unmounts the whole panel, so the date chip is the
              only thing on screen stating which period the money on the grid
              covers. The empty-state text is passed IN rather than tested out
              here so it and the chips read one array — see renderChipRow. */}
          {renderChipRow({
            omitInline: false,
            whenEmpty: <span className="text-[13px] text-rc-muted">Report defaults</span>,
          })}
          {/* `h-[30px] text-[13px]` dropped here too — dead against unlayered
              .btn-rc (38px/14px). `ml-auto` is kept: .btn-rc sets no margin. */}
          <button type="button" onClick={() => setFiltersCollapsed(false)} className="btn-rc ml-auto">
            Edit filters
          </button>
        </div>
      )}

      {/*
        E4 (2026-08-27) — the grid below was computed from filters that have since
        been reset. It sits HERE, immediately above the result, rather than in the
        band slot under the title: it describes the grid, and the query card would
        otherwise stand between the claim and the thing it is about.

        THE LIVE REGION IS THE WRAPPER, AND THE WRAPPER IS ALWAYS MOUNTED.
        A region inserted into the DOM together with its content is not reliably
        announced — the region has to already exist for the insertion to register
        as a change — so gating this whole element on `staleSinceReset` would have
        styled the warning without announcing it. Same shape as the always-present
        announcer at DashboardGrid.jsx:101.

        `role={null}` on the band is deliberate, not an omission. ReportBand
        defaults to role="status", which is itself a polite live region; nesting
        one inside this wrapper leaves it undefined which region owns the change.
        The wrapper announces, the band renders. (A default parameter only fills
        in for `undefined`, so `null` is what actually suppresses the attribute.)

        Not colour alone (WCAG 1.4.1). CORRECTED R3 (2026-08-28) — this used to
        count three signals: glyph, prose, run-label. It was two, because the
        glyph was byte-identical to the `info` variant's ⓘ and so distinguished
        nothing; the criterion was being carried by the PROSE alone, with the run
        control's label ("Run report" again instead of "Refresh", via hasRun
        above) as a second, corroborating one.

        What satisfies 1.4.1 is still the sentence — it names both what happened
        and what the grid is showing, in text, and it would do so if the band
        rendered greyscale. ReportBand's `stale` glyph is now ↻ rather than ⓘ, so
        the glyph is a real third signal again; note it is aria-hidden, which is
        correct and irrelevant here — 1.4.1 is about what a sighted user can
        perceive without colour, not about the accessibility tree. Wording
        follows C3 — prose names the control the way the control spells itself.
      */}
      {/* testid, not a query for [aria-live]: the export menu publishes its own
          polite region, so a bare attribute selector would be testing whichever
          one happened to be in the DOM. */}
      <div aria-live="polite" data-print-hide data-testid="report-stale-live">
        {!running && staleSinceReset && (
          <ReportBand variant="stale" role={null} className="mb-4">
            <span className="font-semibold">Filters were reset</span>
            {' — '}the results below were run with the previous filters and have not
            been updated. Click Run report to apply the filters shown above.
          </ReportBand>
        )}
        {/* Spec 176 (CC-54206) — E4's sentence for the case E4 never covered: the user
            DID run the new filters, and that run has not replaced the rows (it failed,
            was handed to the queue, was dismissed, or stopped reporting). Shown wherever
            the result is (`showResult`, so the hand-off's "table back" is labelled too),
            and never beside E4's own band. It names the scope the rows WERE run with —
            the same run-criteria line the print masthead and the export header carry. */}
        {showResult && supersededScope && !staleSinceReset && (
          <ReportBand variant="stale" role={null} className="mb-4" data-testid="report-superseded">
            <span className="font-semibold">Showing earlier results</span>
            {' — '}the figures below were run with different filters and do not match the
            filters shown above. Your latest run has not replaced them.
            {documentFiltersSummary ? ` They were run with: ${documentFiltersSummary}.` : ''}
          </ReportBand>
        )}
      </div>

      {/* Optional report header HTML */}
      {showResult && reportData?.showReportHeader && reportData?.reportHeaderContent && (
        <div
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 text-sm prose prose-sm max-w-none"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: reportData.reportHeaderContent }}
        />
      )}

      {/* spec 092 — "Bar 3", the result strip (Figma 110:11804).
          The designer's rule for this row, from the note beside it: "These stay
          down on the table because they only change how the table looks."
          So Group by and Saved views left for the query card, Export went up to
          Bar 1, and what remains is the row count plus controls that only affect
          how the result is drawn. */}
      {/* NOTE — the row count is NOT rendered here, deliberately. The shared
          DataGrid's own toolbar already prints "N rows" immediately below, and
          two row counts one line apart is worse than the loose row this replaces.
          That toolbar IS Bar 3 in embryo; turning it into the mock's strip means
          restyling `common/DataGrid`, which nine non-report pages also wear, so it
          is its own piece rather than a side effect of this one. */}
      {/* "Rename columns" has moved ONTO the grid's result strip, immediately
          left of Display View — it is a column control and belongs with the
          other column controls. It travels there as `stripActions` on the
          ReportRunStateContext provider below, the same publish-and-read trick
          `hasRun` uses, because the specialized host cannot pass a prop to a grid
          it renders as `{children}`. What remains here is the chart badge, which
          is not a control at all. */}
      {showResult && reportData && hasChart && (
        <div data-print-hide className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="rounded border border-rc-chip-line bg-rc-chip-bg px-2 py-0.5 text-[11px] font-medium text-rc-chip-text">
            Chart available
          </span>
        </div>
      )}

      {/* AI panel — gated by the per-user AI mode (off/summary/chat); summarizes
          the displayed view (respects the user's Group By). */}
      {/* No "Hide ✨" row any more. The panel carries its own × (which calls this
          same setter through onDismiss) and the AI Summary button in Bar 1
          toggles it — a third control for one job was part of the clutter
          Jordan flagged, and it sat in a row of its own above the panel. */}
      {AI_REPORT_SUMMARY && aiMode !== 'off' && !aiCollapsed && showResult && displayReport && (isFlatReport(displayReport) || isGroupedReport(displayReport)) && (
        <div id={AI_PANEL_ID} className="mb-4">
          <ReportAiSummary
            /* Spec 131: keyed on the COMMITTED result's identity. This panel stays
               mounted while a run is handed off (showResult is true), and its
               auto-start runs once per mount — so a result landing under it kept
               the summary or chat about the PREVIOUS rows, beneath a scope line
               that had already switched to the new run's criteria. */
            key={runResult?.seq ?? 'none'}
            mode={aiMode}
            getView={() => exportViewRef.current?.()}
            reportName={definition.name}
            /* The RUN's criteria: the panel below feeds this model the grid's
               own figures, so the scope line must describe those figures. */
            scope={aiScopeLine(documentFiltersSummary, reportData?.recordCount)}
            onDismiss={() => setAiCollapsed(true)}
          />
        </div>
      )}

      {/* Data table — flat reports → ReportDataGrid; grouped reports →
          ReportGroupedGrid (server subtotals/totals preserved, within-group
          controls layered on); anything else falls back to ReportTable.
          The CC-49993 oversized interstitial ("This report returned N rows /
          Show all N rows anyway") was removed on 2026-08-07: the grid paginates,
          and a large result now hands off to the queue instead of blocking the
          page, so a second gate in front of the render only added a click. The
          export path is unaffected — it still reads the grid's export view, and
          the grid is now always mounted, so client-scope export works on a big
          result where it previously had no view to read. */}
      {showResult && displayReport && (
        <ReportRunStateContext.Provider value={{ hasRun, stripActions }}>
        {isFlatReport(displayReport) ? (
          <ReportDataGrid
            key={`${definition.actionKey || reportId}:${viewNonce}:${groupBy}`}
            report={displayReport}
            storageKey={storageKey}
            exportViewRef={exportViewRef}
            onFilteredChange={setGridFiltered}
            pageStateRef={pageStateRef}
            initialPageState={appliedGridStateRef.current}
          />
        ) : isGroupedReport(displayReport) ? (
          <ReportGroupedGrid
            key={`${definition.actionKey || reportId}:${viewNonce}:${groupBy}`}
            report={displayReport}
            storageKey={storageKey}
            exportViewRef={exportViewRef}
            onFilteredChange={setGridFiltered}
            pageStateRef={pageStateRef}
            initialPageState={appliedGridStateRef.current}
          />
        ) : isEmptyResult(displayReport) ? (
          /* Ran, returned nothing. Keep the grid so the page still says "0 rows"
             and still offers Display View / Column Filter / export scope — an
             empty result and a broken page must not look the same. */
          <ReportDataGrid
            key={`${definition.actionKey || reportId}:${viewNonce}:${groupBy}:empty`}
            report={displayReport}
            storageKey={storageKey}
            exportViewRef={exportViewRef}
            onFilteredChange={setGridFiltered}
            pageStateRef={pageStateRef}
            initialPageState={appliedGridStateRef.current}
          />
        ) : (
          /* ReportTable has no result strip to host strip actions, so the button
             would simply vanish on this branch — it is present here today. Keep
             it in a row of its own for exactly this case, rather than losing a
             capability to a layout change. */
          <>
            {stripActions && (
              <div data-print-hide className="mb-2 flex justify-end">{stripActions}</div>
            )}
            <ReportTable report={displayReport} />
          </>
        )}
        </ReportRunStateContext.Provider>
      )}

      {/* Optional report footer HTML */}
      {showResult && reportData?.showReportFooter && reportData?.reportFooterContent && (
        <div
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mt-4 text-sm prose prose-sm max-w-none"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: reportData.reportFooterContent }}
        />
      )}

      {!running && !reportData && !runError && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-sm text-gray-500">
          {/* C3 (2026-08-27) — these name the run control, so they spell it the
              way the control spells itself: sentence-case "Run report"
              (ReportFilterPanel.jsx:1381, the `label` const — `runLabel ||
              (hasRun ? 'Refresh' : 'Run report')`. Cited as :1120 until
              2026-08-27; re-measured, and named by symbol as well as by line so
              the next drift is recoverable). Both branches are pre-run by
              construction (`!reportData`), so "Run report" is the label actually
              on screen, never "Refresh".

              Changing THESE is safe precisely because they are prose, not the
              button: the button's own label is frozen by e2e locators
              (`/^(Run report|Refresh|Filter)$/`, reports-grid-audit.spec.js:145
              and `/^(Run report|Refresh)$/` financial-reports-audit.spec.js:169)
              and must not be touched. The one assertion on this copy pins the
              full string (ReportView.test.jsx:238), upstream of the edit.

              E4 (2026-08-27) added a THIRD piece of prose naming this control —
              the stale-grid band above, which says "Click Run report to apply the
              filters shown above". Same rule, and it is the reason the band
              carries no button of its own: a second control named "Run report"
              would make the e2e locators above ambiguous under strict mode. */}
          {definition.runOnOpen === false
            ? 'Choose filters above and click Run report'
            : 'Click Run report to view data'}
        </div>
      )}

      {/* Entry point 1 of 2 for the firm-vocabulary editor (spec 061 O3):
          inline, on the report, so an admin renames a column while looking at
          it. Entry point 2 is Settings → Reports; both open this same editor
          against the same cache. Gated on `canManage` from the alias read, so
          it never appears for a non-admin. */}
      {renamingColumns && (
        <ColumnAliasEditor
          reportKey={reportId}
          reportName={definition.name}
          columns={definition.columns || []}
          onClose={() => setRenamingColumns(false)}
        />
      )}

      {/* Scheduling opened from the Run split button. Same modal the Queue page
          uses, minus the report picker — the report is already chosen, and its
          on-screen filters ride along. */}
      {scheduleWhen && (
        <QueueReportModal
          report={{ id: reportId, name: definition.name }}
          columns={(definition.columns || []).filter((c) => c.filterable !== false && c.filterTypeName && c.filterTypeName !== 'HIDDEN')}
          values={filterValues}
          onChange={onFilterChange}
          accountIds={definition.filteredAccountIds}
          initialWhen={scheduleWhen}
          // The EXACT wire array the create call will send (CC-50232 F1), so the
          // server's roll-forward preview is computed from the same bytes that
          // get stored. Deriving an equivalent array inside the modal would be a
          // second source of truth, which is what this server-computed preview
          // exists to avoid.
          scheduleFilters={activeFilters}
          isSubmitting={scheduleSubmitting}
          onQueueNow={(delivery) => runReportWith(filterValues, { background: true, deliver: delivery, trigger: 'queue_now' })}
          onQueueLater={(at, delivery) => submitSchedule('later', at, delivery)}
          onSchedule={(cfg) => submitSchedule('schedule', cfg)}
          onClose={() => setScheduleWhen(null)}
        />
      )}
    </div>
  )
}

// Wrap in a report-scoped error boundary so a single broken report doesn't
// crash into the global ErrorBoundary and force a full navigation home.
const ReportView = ({ backTo } = {}) => (
  <ReportErrorBoundary>
    <ReportViewInner backTo={backTo} />
  </ReportErrorBoundary>
)

export default ReportView
